import { Application, Container, Graphics, Text, TextStyle, Texture, Rectangle, Sprite, AnimatedSprite } from 'pixi.js';
import { Scene } from '@shared/engine/SceneManager';
import { ProgressBar } from '@shared/ui/ProgressBar';
import {
  GameState, COOK_TIME, CARNE_SELL_VALUE,
  ANIMAL_HP, ANIMAL_RESPAWN, MAX_ANIMALS,
  CLIENT_PATIENCE, MAX_CLIENTS_QUEUE,
  WORKER_SPEED, BAG_UPGRADE_COST,
  GAME_DURATION, FENCE, TOWER, REST_AREA, ORBITAL_SHIELD,
  HERO_STATS, MONSTER_TYPES, MONSTER_SPAWN, MONSTER_WAVES, MONSTER_LOOT, MonsterType,
  InventoryType, WorkerType,
} from '../GameState';
import { InputManager } from '../systems/InputManager';
import { CameraSystem } from '../systems/CameraSystem';
import { COLORS, SIZES, MAP, SPRITES, CHAR_SHEET, MONSTER_SHEET, SPRITE_SCALE, VIGOR } from '../config/assets';

// ─── Constantes de Gameplay ───
const PLAYER_SPEED = 150;
const PROXIMITY_ATTACK = 50;
const PROXIMITY_PICKUP = 30;
const PROXIMITY_DEPOSIT = 40;
const PROXIMITY_MONEY = 25;
const ATTACK_COOLDOWN = 0.5;
const ATTACK_DAMAGE = 1;
const CLIENT_SPAWN_BASE_INTERVAL = 6;
const CLIENT_EAT_TIME = 2;
const CACADOR_SPEED = 70;

type Dir = 'down' | 'up' | 'left' | 'right';

// ─── Sprite Helpers ───

function getCharFrames(spritePath: string, row: number, cols = CHAR_SHEET.cols): Texture[] {
  const base = Texture.from(spritePath);
  const frames: Texture[] = [];
  for (let c = 0; c < cols; c++) {
    frames.push(new Texture({
      source: base.source,
      frame: new Rectangle(
        c * CHAR_SHEET.frameW,
        row * CHAR_SHEET.frameH,
        CHAR_SHEET.frameW,
        CHAR_SHEET.frameH,
      ),
    }));
  }
  return frames;
}

function getMonsterFrames(spritePath: string, row: number): Texture[] {
  const base = Texture.from(spritePath);
  const frames: Texture[] = [];
  for (let c = 0; c < MONSTER_SHEET.cols; c++) {
    frames.push(new Texture({
      source: base.source,
      frame: new Rectangle(
        c * MONSTER_SHEET.frameW,
        row * MONSTER_SHEET.frameH,
        MONSTER_SHEET.frameW,
        MONSTER_SHEET.frameH,
      ),
    }));
  }
  return frames;
}

function dirToRow(dir: Dir): number {
  switch (dir) {
    case 'down': return CHAR_SHEET.rowDown;
    case 'up': return CHAR_SHEET.rowUp;
    case 'left': return CHAR_SHEET.rowLeft;
    case 'right': return CHAR_SHEET.rowRight;
  }
}

const frameCache = new Map<string, Texture[]>();
function getCachedFrames(path: string, row: number, isMonster = false): Texture[] {
  const key = `${path}:${row}`;
  if (!frameCache.has(key)) {
    frameCache.set(key, isMonster ? getMonsterFrames(path, row) : getCharFrames(path, row));
  }
  return frameCache.get(key)!;
}

function createCharAnim(spritePath: string, dir: Dir, scale: number): AnimatedSprite {
  const row = dirToRow(dir);
  const frames = getCachedFrames(spritePath, row);
  const anim = new AnimatedSprite(frames);
  anim.anchor.set(0.5, 0.7);
  anim.scale.set(scale);
  anim.animationSpeed = 0.12;
  anim.play();
  return anim;
}

function updateCharDir(anim: AnimatedSprite, spritePath: string, dir: Dir) {
  const row = dirToRow(dir);
  const frames = getCachedFrames(spritePath, row);
  if (anim.textures !== frames) {
    anim.textures = frames;
    anim.play();
  }
}

// ─── Interfaces ───

interface Animal {
  gfx: Container;
  anim: AnimatedSprite;
  x: number;
  y: number;
  hp: number;
  maxHp: number;
  alive: boolean;
  respawnTimer: number;
  hpBar: ProgressBar;
  wanderTimer: number;
  wanderDx: number;
  wanderDy: number;
  homeX: number;
  homeY: number;
  hitFlash: number;
  claimed: boolean;
  dir: Dir;
}

interface GroundItem {
  gfx: Sprite;
  x: number;
  y: number;
  type: InventoryType;
  bobTimer: number;
  claimed: boolean;
}

type WorkerState =
  | 'idle'
  | 'seeking' | 'attacking' | 'cooldown'
  | 'seeking_meat' | 'picking_up'
  | 'walking_to_assador' | 'walking_to_balcao'
  | 'walking_to_client' | 'serving'
  | 'returning_to_assador'
  | 'seeking_coin' | 'walking_to_banco'
  | 'depositing'
  | 'walking_to_rest' | 'resting';

interface WorkerNPC {
  gfx: Container;
  anim: AnimatedSprite;
  spritePath: string;
  type: WorkerType;
  x: number;
  y: number;
  dir: Dir;
  state: WorkerState;
  stateTimer: number;
  targetX: number;
  targetY: number;
  carrying: number;
  carryText: Text;
  stateText: Text;
  targetAnimal: Animal | null;
  targetItem: GroundItem | null;
  targetClient: Client | null;
  targetCoin: CoinItem | null;
  attackCooldown: number;
  vigor: number;
  vigorBar: ProgressBar;
  preRestState: WorkerState;
}

interface HireZone {
  gfx: Container;
  type: WorkerType;
  x: number;
  y: number;
  hired: boolean;
  labelText: Text;
  priceText: Text;
}

interface CoinItem {
  gfx: Sprite;
  x: number;
  y: number;
  bobTimer: number;
  claimed: boolean;
}

interface Client {
  gfx: Container;
  anim: AnimatedSprite;
  x: number;
  y: number;
  state: 'walking' | 'waiting' | 'eating' | 'leaving';
  timer: number;
  targetX: number;
  patience: number;
  maxPatience: number;
  served: boolean;
  queueIndex: number;
  patienceBar: ProgressBar;
  claimedByGarcom: boolean;
}

interface Monster {
  gfx: Container;
  anim: AnimatedSprite;
  x: number;
  y: number;
  hp: number;
  maxHp: number;
  dano: number;
  monsterType: MonsterType;
  hpBar: ProgressBar;
  dir: Dir;
  alive: boolean;
  hitFlash: number;
  attackTimer: number;
  focaCerca: boolean;
  ignoraCerca: boolean;
  insideFence: boolean;
  isElite: boolean;
}

interface UpgradeZone {
  gfx: Container;
  upgradeType: 'player' | 'ajudante' | 'garcom' | 'contador';
  x: number;
  y: number;
  labelText: Text;
  priceText: Text;
  capacityText: Text;
  playerInside: boolean;
}

interface FloatingText {
  text: Text;
  timer: number;
  vy: number;
}

const CLIENT_SPRITES = [SPRITES.client1, SPRITES.client2, SPRITES.client3];

const MONSTER_SPRITE_MAP: Record<MonsterType, string> = {
  selvagem: SPRITES.selvagem,
  agressivo: SPRITES.agressivo,
  destruidor: SPRITES.destruidor,
  voador: SPRITES.voador,
};

// ─── Cena Principal ───
export class GameplayScene extends Scene {
  private gs: GameState;
  private input: InputManager;
  private camera: CameraSystem;

  // Layers
  private worldContainer!: Container;
  private terrainLayer!: Container;
  private stationLayer!: Container;
  private entityLayer!: Container;
  private itemLayer!: Container;
  private uiLayer!: Container;

  // Player
  private playerGfx!: Container;
  private playerAnim!: AnimatedSprite;
  private playerCarryText!: Text;
  private playerX: number = MAP.playerSpawn.x;
  private playerY: number = MAP.playerSpawn.y;
  private playerDir: Dir = 'down';
  private lastPlayerDir: Dir = 'down';
  private attackCooldown = 0;
  private playerMoving = false;

  // Entities
  private animals: Animal[] = [];
  private groundItems: GroundItem[] = [];
  private coins: CoinItem[] = [];
  private clients: Client[] = [];
  private monsters: Monster[] = [];
  private floatingTexts: FloatingText[] = [];

  // Station visuals
  private assadorGfx!: Container;
  private assadorFlames!: Graphics;
  private balcaoGfx!: Container;
  private mesaCarneText!: Text;
  private fogueiraBar!: ProgressBar;
  private bancadaCarneText!: Text;
  private balcaoCarneText!: Text;

  // Fence visuals
  private fenceGfx!: Graphics;
  private fenceHpBar!: ProgressBar;
  private fenceHpText!: Text;
  private towerGfxList: Container[] = [];

  // Client spawn
  private clientSpawnTimer = CLIENT_SPAWN_BASE_INTERVAL;

  // Monster spawn
  private monsterSpawnTimer: number = 45; // primeiro spawn apos 45s
  private agressivoCount = 0;

  // Game over flag
  private gameOver = false;
  private gameOverGfx: Container | null = null;
  private lastWaveIndex = -1;

  // HUD
  private hudMoney!: Text;
  private hudBackpack!: Text;
  private hudAbates!: Text;
  private hudTimer!: Text;
  private hudCerca!: Text;
  private hudWorkers!: Text;
  private hudPrata!: Text;

  // Workers
  private workers: WorkerNPC[] = [];
  private hireZones: HireZone[] = [];
  private upgradeZones: UpgradeZone[] = [];

  // Rest area visual
  private restAreaGfx!: Container;

  // Player combat
  private playerHpBar!: ProgressBar;
  private playerRespawnTimer = 0;
  private playerDead = false;

  // Escudo orbital
  private shieldGfx: Graphics | null = null;
  private shieldAngle = 0;

  // Upgrade station zones (near banco)
  private upgradeStationZones: { gfx: Container; type: string; x: number; y: number; labelText: Text; priceText: Text; playerInside: boolean }[] = [];

  // Repair cooldown
  private repairCooldown = 0;

  // Pickup feedback cooldown (avoid spam)
  private pickupFeedbackCooldown = 0;

  // Click hitarea
  private clickHitArea!: Graphics;

  constructor(app: Application, gameState: GameState) {
    super(app);
    this.gs = gameState;
    this.input = new InputManager(app);
    this.camera = new CameraSystem(new Container(), app.screen.width, app.screen.height, MAP.worldW, MAP.worldH);
  }

  onEnter() {
    this.buildWorld();
    this.buildFence();
    this.buildTowers();
    this.buildStations();
    this.buildRestArea();
    this.buildPlayer();
    this.buildHireZones();
    this.buildUpgradeZones();
    this.buildDefenseUpgradeZones();
    this.buildHUD();
    this.spawnAnimals();
    this.restoreWorkers();
    if (this.gs.hasShield) this.buildShieldVisual();
    this.setupInput();

    this.camera = new CameraSystem(this.worldContainer, this.app.screen.width, this.app.screen.height, MAP.worldW, MAP.worldH);
    this.camera.snapTo(this.playerX, this.playerY, 60);
  }

  onExit() {
    this.input.destroy();
  }

  // ─── Build Methods ───

  private buildWorld() {
    this.worldContainer = new Container();
    this.worldContainer.sortableChildren = true;
    this.addChild(this.worldContainer);

    this.terrainLayer = new Container();
    this.terrainLayer.zIndex = 0;
    this.worldContainer.addChild(this.terrainLayer);

    this.stationLayer = new Container();
    this.stationLayer.zIndex = 1;
    this.worldContainer.addChild(this.stationLayer);

    this.itemLayer = new Container();
    this.itemLayer.zIndex = 2;
    this.worldContainer.addChild(this.itemLayer);

    this.entityLayer = new Container();
    this.entityLayer.zIndex = 3;
    this.worldContainer.addChild(this.entityLayer);

    this.uiLayer = new Container();
    this.uiLayer.zIndex = 100;
    this.addChild(this.uiLayer);

    this.drawTerrain();
  }

  private drawTerrain() {
    const EXT = 300;

    // Full background grass
    const bgGrass = new Graphics();
    bgGrass.rect(-EXT, -EXT, MAP.worldW + EXT * 2, MAP.worldH + EXT * 2).fill({ color: COLORS.grass });
    this.terrainLayer.addChild(bgGrass);

    // Dirt inside fence area
    const fenceArea = new Graphics();
    fenceArea.rect(MAP.fence.x, MAP.fence.y, MAP.fence.w, MAP.fence.h).fill({ color: COLORS.dirt });
    this.terrainLayer.addChild(fenceArea);

    // Dirt path from gate to client zone
    const gatePath = new Graphics();
    gatePath.rect(
      MAP.fence.gateX - MAP.fence.gateW / 2,
      MAP.fence.gateY,
      MAP.fence.gateW,
      MAP.clientZone.y - MAP.fence.gateY + 30,
    ).fill({ color: COLORS.dirtLight });
    this.terrainLayer.addChild(gatePath);

    // Hunting zone highlight (subtle)
    const huntZone = new Graphics();
    huntZone.rect(MAP.huntingZone.x, MAP.huntingZone.y, MAP.huntingZone.w, MAP.huntingZone.h)
      .fill({ color: COLORS.grassLight, alpha: 0.4 });
    this.terrainLayer.addChild(huntZone);

    // Trees around edges
    for (let i = 0; i < 12; i++) {
      const tx = 20 + Math.random() * (MAP.worldW - 40);
      const ty = 10 + Math.random() * (MAP.huntingZone.h - 20);
      this.terrainLayer.addChild(this.createTreeGfx(tx, ty));
    }
    // Trees on sides
    for (let i = 0; i < 6; i++) {
      const side = Math.random() < 0.5;
      const tx = side ? 20 + Math.random() * 150 : MAP.worldW - 170 + Math.random() * 150;
      const ty = MAP.fence.y + Math.random() * MAP.fence.h;
      this.terrainLayer.addChild(this.createTreeGfx(tx, ty));
    }
  }

  private createTreeGfx(x: number, y: number): Container {
    const c = new Container();
    c.x = x;
    c.y = y;
    const trunk = new Graphics();
    trunk.rect(-4, -4, 8, 16).fill({ color: COLORS.treeTrunk });
    c.addChild(trunk);
    const top = new Graphics();
    top.circle(0, -12, 14).fill({ color: COLORS.tree, alpha: 0.85 });
    c.addChild(top);
    return c;
  }

  private buildFence() {
    this.fenceGfx = new Graphics();
    this.fenceGfx.zIndex = 1;
    this.drawFence();
    this.stationLayer.addChild(this.fenceGfx);

    // Fence HP bar (world-space, above fence top, centered)
    this.fenceHpBar = new ProgressBar({
      width: 160, height: 10,
      fillColor: 0x44cc44, bgColor: 0x333333,
      showText: false,
    });
    this.fenceHpBar.x = MAP.fence.centerX - 80;
    this.fenceHpBar.y = MAP.fence.y - 24;
    this.fenceHpBar.setProgress(this.gs.cercaHp / this.gs.cercaHpMax);
    this.stationLayer.addChild(this.fenceHpBar);

    this.fenceHpText = new Text({
      text: `Cerca: ${this.gs.cercaHp}/${this.gs.cercaHpMax}`,
      style: new TextStyle({ fontSize: 11, fill: '#ffffff', fontWeight: 'bold' }),
    });
    this.fenceHpText.anchor.set(0.5, 0);
    this.fenceHpText.x = MAP.fence.centerX;
    this.fenceHpText.y = MAP.fence.y - 38;
    this.stationLayer.addChild(this.fenceHpText);

    // Repair label near gate
    const repairLabel = new Text({
      text: `Reparo: ${FENCE.reparoCustoBronze}B`,
      style: new TextStyle({ fontSize: 9, fill: '#88ff88' }),
    });
    repairLabel.anchor.set(0.5);
    repairLabel.x = MAP.fence.gateX;
    repairLabel.y = MAP.fence.gateY + 35;
    this.stationLayer.addChild(repairLabel);
  }

  private drawFence() {
    this.fenceGfx.clear();
    const f = MAP.fence;
    const hpPct = this.gs.cercaHp / this.gs.cercaHpMax;
    const color = hpPct > 0.5 ? COLORS.cerca : COLORS.cercaDamaged;
    const thickness = 4;

    // Top wall
    this.fenceGfx.rect(f.x, f.y, f.w, thickness).fill({ color });
    // Left wall
    this.fenceGfx.rect(f.x, f.y, thickness, f.h).fill({ color });
    // Right wall
    this.fenceGfx.rect(f.x + f.w - thickness, f.y, thickness, f.h).fill({ color });
    // Bottom wall with gate opening
    const gateLeft = f.gateX - f.gateW / 2;
    const gateRight = f.gateX + f.gateW / 2;
    this.fenceGfx.rect(f.x, f.y + f.h - thickness, gateLeft - f.x, thickness).fill({ color });
    this.fenceGfx.rect(gateRight, f.y + f.h - thickness, f.x + f.w - gateRight, thickness).fill({ color });
  }

  private buildTowers() {
    for (let i = 0; i < MAP.towers.length; i++) {
      const tp = MAP.towers[i];
      const c = new Container();
      c.x = tp.x;
      c.y = tp.y;

      const bg = new Graphics();
      bg.roundRect(-14, -14, 28, 28, 4).fill({ color: COLORS.torre });
      bg.stroke({ color: 0x888888, width: 2 });
      c.addChild(bg);

      const icon = new Text({
        text: 'T',
        style: new TextStyle({ fontSize: 14, fill: '#ffffff', fontWeight: 'bold' }),
      });
      icon.anchor.set(0.5);
      c.addChild(icon);

      // Cost label for unbuilt towers
      const costLabel = new Text({
        text: `${TOWER.custoPrata}P`,
        style: new TextStyle({ fontSize: 9, fill: '#ffcc00' }),
      });
      costLabel.anchor.set(0.5);
      costLabel.y = 20;
      c.addChild(costLabel);

      const built = i < this.gs.torreCount;
      c.alpha = built ? 1.0 : 0.4;
      costLabel.visible = !built;

      this.stationLayer.addChild(c);
      this.towerGfxList.push(c);
    }
  }

  private buildStations() {
    // ─── Mesa do Assador ───
    this.assadorGfx = new Container();
    this.assadorGfx.x = MAP.assador.x;
    this.assadorGfx.y = MAP.assador.y;

    const aw = MAP.assador.w;
    const ah = MAP.assador.h;

    const mesaBody = new Graphics();
    mesaBody.roundRect(-aw / 2, -ah / 2, aw, ah, 6).fill({ color: COLORS.mesaAssador });
    mesaBody.stroke({ color: 0x3a2010, width: 2 });
    this.assadorGfx.addChild(mesaBody);

    const zonaEntrada = new Graphics();
    zonaEntrada.roundRect(-aw / 2 + 4, -ah / 2 + 4, aw / 3 - 8, ah - 8, 3).fill({ color: 0x5a3220 });
    this.assadorGfx.addChild(zonaEntrada);

    const lblCrua = new Text({
      text: 'Crua',
      style: new TextStyle({ fontSize: 9, fill: '#ff8888' }),
    });
    lblCrua.anchor.set(0.5);
    lblCrua.x = -aw / 3;
    lblCrua.y = ah / 2 + 10;
    this.assadorGfx.addChild(lblCrua);

    const fogueiraBase = new Graphics();
    fogueiraBase.circle(0, 0, 16).fill({ color: 0x555555 });
    this.assadorGfx.addChild(fogueiraBase);

    const lenha = new Graphics();
    lenha.rect(-8, -2, 16, 4).fill({ color: COLORS.treeTrunk });
    lenha.rect(-2, -8, 4, 16).fill({ color: COLORS.treeTrunk });
    this.assadorGfx.addChild(lenha);

    this.assadorFlames = new Graphics();
    this.assadorFlames.circle(0, -3, 9).fill({ color: COLORS.fogueira, alpha: 0.8 });
    this.assadorFlames.circle(-3, -7, 5).fill({ color: 0xffcc00, alpha: 0.7 });
    this.assadorFlames.circle(3, -5, 4).fill({ color: 0xff6600, alpha: 0.6 });
    this.assadorGfx.addChild(this.assadorFlames);

    const zonaSaida = new Graphics();
    zonaSaida.roundRect(aw / 2 - aw / 3 + 4, -ah / 2 + 4, aw / 3 - 8, ah - 8, 3).fill({ color: 0x7a5230 });
    this.assadorGfx.addChild(zonaSaida);

    const lblAssada = new Text({
      text: 'Assada',
      style: new TextStyle({ fontSize: 9, fill: '#ffcc66' }),
    });
    lblAssada.anchor.set(0.5);
    lblAssada.x = aw / 3;
    lblAssada.y = ah / 2 + 10;
    this.assadorGfx.addChild(lblAssada);

    const arrow = new Text({ text: '\u2192', style: new TextStyle({ fontSize: 14, fill: '#ffffff88' }) });
    arrow.anchor.set(0.5);
    arrow.x = -aw / 6;
    this.assadorGfx.addChild(arrow);

    const arrow2 = new Text({ text: '\u2192', style: new TextStyle({ fontSize: 14, fill: '#ffffff88' }) });
    arrow2.anchor.set(0.5);
    arrow2.x = aw / 6;
    this.assadorGfx.addChild(arrow2);

    const assadorLabel = new Text({
      text: 'Mesa do Assador',
      style: new TextStyle({ fontSize: 11, fill: '#ffffff', fontWeight: 'bold' }),
    });
    assadorLabel.anchor.set(0.5);
    assadorLabel.y = -ah / 2 - 12;
    this.assadorGfx.addChild(assadorLabel);

    this.stationLayer.addChild(this.assadorGfx);

    // Texto carne crua
    this.mesaCarneText = new Text({
      text: '',
      style: new TextStyle({ fontSize: 12, fill: '#ff6666', fontWeight: 'bold' }),
    });
    this.mesaCarneText.anchor.set(0.5);
    this.mesaCarneText.x = MAP.assador.entradaX;
    this.mesaCarneText.y = MAP.assador.y - ah / 2 - 24;
    this.stationLayer.addChild(this.mesaCarneText);

    // ProgressBar de cozimento
    this.fogueiraBar = new ProgressBar({
      width: 40, height: 6,
      fillColor: 0xff4500, bgColor: 0x333333,
      showText: false,
    });
    this.fogueiraBar.x = MAP.assador.fogueiraX - 20;
    this.fogueiraBar.y = MAP.assador.y - ah / 2 - 24;
    this.fogueiraBar.visible = false;
    this.stationLayer.addChild(this.fogueiraBar);

    // Texto carne assada
    this.bancadaCarneText = new Text({
      text: '',
      style: new TextStyle({ fontSize: 12, fill: '#ffaa00', fontWeight: 'bold' }),
    });
    this.bancadaCarneText.anchor.set(0.5);
    this.bancadaCarneText.x = MAP.assador.saidaX;
    this.bancadaCarneText.y = MAP.assador.y - ah / 2 - 24;
    this.stationLayer.addChild(this.bancadaCarneText);

    // ─── Balcao de Venda ───
    this.balcaoGfx = new Container();
    this.balcaoGfx.x = MAP.balcaoVenda.x;
    this.balcaoGfx.y = MAP.balcaoVenda.y;

    const bw = SIZES.station.w;
    const bh = SIZES.station.h;
    const balcaoBody = new Graphics();
    balcaoBody.roundRect(-bw / 2, -bh / 2, bw, bh, 4).fill({ color: COLORS.balcao });
    balcaoBody.stroke({ color: 0x2a5a3a, width: 2 });
    this.balcaoGfx.addChild(balcaoBody);

    const balcaoLabel = new Text({
      text: 'Balcao de Venda',
      style: new TextStyle({ fontSize: 10, fill: '#ffffff', fontWeight: 'bold' }),
    });
    balcaoLabel.anchor.set(0.5);
    balcaoLabel.y = bh / 2 + 8;
    this.balcaoGfx.addChild(balcaoLabel);

    this.stationLayer.addChild(this.balcaoGfx);

    // ─── Predio do Banco ───
    const bancoGfx = new Container();
    bancoGfx.x = MAP.banco.x;
    bancoGfx.y = MAP.banco.y;

    const bwBanco = MAP.banco.w;
    const bhBanco = MAP.banco.h;
    const bancoBody = new Graphics();
    bancoBody.roundRect(-bwBanco / 2, -bhBanco / 2, bwBanco, bhBanco, 4).fill({ color: COLORS.banco });
    bancoBody.stroke({ color: 0x2d3748, width: 2 });
    bancoGfx.addChild(bancoBody);

    const bancoRoof = new Graphics();
    bancoRoof.rect(-bwBanco / 2 - 4, -bhBanco / 2 - 6, bwBanco + 8, 8).fill({ color: 0x2d3748 });
    bancoGfx.addChild(bancoRoof);

    const bancoDoor = new Graphics();
    bancoDoor.roundRect(-7, 4, 14, 20, 2).fill({ color: 0x744210 });
    bancoGfx.addChild(bancoDoor);

    const bancoIcon = new Text({
      text: '$',
      style: new TextStyle({ fontSize: 16, fill: '#ffd700', fontWeight: 'bold' }),
    });
    bancoIcon.anchor.set(0.5);
    bancoIcon.y = -10;
    bancoGfx.addChild(bancoIcon);

    const bancoLabel = new Text({
      text: 'Banco',
      style: new TextStyle({ fontSize: 11, fill: '#ffffff', fontWeight: 'bold' }),
    });
    bancoLabel.anchor.set(0.5);
    bancoLabel.y = bhBanco / 2 + 8;
    bancoGfx.addChild(bancoLabel);

    this.stationLayer.addChild(bancoGfx);

    // Texto de carne no balcao
    this.balcaoCarneText = new Text({
      text: '',
      style: new TextStyle({ fontSize: 12, fill: '#ffaa00', fontWeight: 'bold' }),
    });
    this.balcaoCarneText.anchor.set(0.5);
    this.balcaoCarneText.x = MAP.balcaoVenda.x;
    this.balcaoCarneText.y = MAP.balcaoVenda.y - bh / 2 - 14;
    this.stationLayer.addChild(this.balcaoCarneText);
  }

  private buildRestArea() {
    this.restAreaGfx = new Container();
    this.restAreaGfx.x = MAP.restArea.x;
    this.restAreaGfx.y = MAP.restArea.y;

    const rw = MAP.restArea.w;
    const rh = MAP.restArea.h;
    const bg = new Graphics();
    bg.roundRect(-rw / 2, -rh / 2, rw, rh, 6).fill({ color: 0x5a7a4a, alpha: 0.4 });
    bg.stroke({ color: 0x3a5a2a, width: 2 });
    this.restAreaGfx.addChild(bg);

    const restLabel = new Text({
      text: 'Descanso',
      style: new TextStyle({ fontSize: 10, fill: '#88cc88', fontWeight: 'bold' }),
    });
    restLabel.anchor.set(0.5);
    restLabel.y = rh / 2 + 6;
    this.restAreaGfx.addChild(restLabel);

    const zzz = new Text({
      text: 'zzZ',
      style: new TextStyle({ fontSize: 14, fill: '#aaddaa' }),
    });
    zzz.anchor.set(0.5);
    zzz.y = -10;
    this.restAreaGfx.addChild(zzz);

    this.stationLayer.addChild(this.restAreaGfx);
  }

  private buildPlayer() {
    this.playerGfx = new Container();

    this.playerAnim = createCharAnim(SPRITES.player, 'down', SPRITE_SCALE.character);
    this.playerGfx.addChild(this.playerAnim);

    const name = new Text({
      text: 'Caua',
      style: new TextStyle({ fontSize: 10, fill: '#ffffff', fontWeight: 'bold' }),
    });
    name.anchor.set(0.5);
    name.y = -SIZES.player - 14;
    this.playerGfx.addChild(name);

    this.playerCarryText = new Text({
      text: '',
      style: new TextStyle({ fontSize: 9, fill: '#ffcc00', fontWeight: 'bold' }),
    });
    this.playerCarryText.anchor.set(0.5);
    this.playerCarryText.y = -SIZES.player - 26;
    this.playerGfx.addChild(this.playerCarryText);

    // Player HP bar
    this.playerHpBar = new ProgressBar({
      width: 32, height: 4,
      fillColor: 0x44cc44, bgColor: 0x333333,
      showText: false,
    });
    this.playerHpBar.x = -16;
    this.playerHpBar.y = -SIZES.player - 8;
    this.playerHpBar.setProgress(1);
    this.playerGfx.addChild(this.playerHpBar);

    this.entityLayer.addChild(this.playerGfx);
  }

  private buildHUD() {
    const style = new TextStyle({
      fontSize: 16,
      fill: '#ffffff',
      fontWeight: 'bold',
      fontFamily: 'Arial, sans-serif',
      dropShadow: { color: '#000000', blur: 2, distance: 1 } as any,
    });

    // HUD background panel
    const hudBg = new Graphics();
    hudBg.roundRect(4, 4, 260, 120, 6).fill({ color: 0x000000, alpha: 0.5 });
    this.uiLayer.addChild(hudBg);

    this.hudMoney = new Text({ text: 'Bronze: 0', style });
    this.hudMoney.x = 12;
    this.hudMoney.y = 8;
    this.uiLayer.addChild(this.hudMoney);

    this.hudPrata = new Text({ text: '', style: new TextStyle({ ...style, fontSize: 14, fill: '#c0c0c0' }) });
    this.hudPrata.x = 12;
    this.hudPrata.y = 28;
    this.uiLayer.addChild(this.hudPrata);

    this.hudBackpack = new Text({ text: 'Mochila: vazia', style: new TextStyle({ ...style, fontSize: 13 }) });
    this.hudBackpack.x = 12;
    this.hudBackpack.y = 48;
    this.uiLayer.addChild(this.hudBackpack);

    this.hudAbates = new Text({ text: 'Abates: 0', style: new TextStyle({ ...style, fontSize: 11, fill: '#aaaaaa' }) });
    this.hudAbates.x = 12;
    this.hudAbates.y = 66;
    this.uiLayer.addChild(this.hudAbates);

    // Timer background
    const timerBg = new Graphics();
    timerBg.roundRect(this.app.screen.width / 2 - 50, 4, 100, 30, 6).fill({ color: 0x000000, alpha: 0.6 });
    this.uiLayer.addChild(timerBg);

    this.hudTimer = new Text({ text: '', style: new TextStyle({ ...style, fontSize: 20, fill: '#44ff44' }) });
    this.hudTimer.anchor.set(0.5, 0);
    this.hudTimer.x = this.app.screen.width / 2;
    this.hudTimer.y = 8;
    this.uiLayer.addChild(this.hudTimer);

    this.hudCerca = new Text({ text: '', style: new TextStyle({ ...style, fontSize: 11, fill: '#ffcc00' }) });
    this.hudCerca.x = 12;
    this.hudCerca.y = 82;
    this.uiLayer.addChild(this.hudCerca);

    this.hudWorkers = new Text({ text: '', style: new TextStyle({ ...style, fontSize: 11, fill: '#aaddff' }) });
    this.hudWorkers.x = 12;
    this.hudWorkers.y = 98;
    this.uiLayer.addChild(this.hudWorkers);
  }

  private setupInput() {
    this.clickHitArea = new Graphics();
    this.clickHitArea.rect(0, 0, MAP.worldW, MAP.worldH).fill({ color: 0x000000, alpha: 0.001 });
    this.worldContainer.addChildAt(this.clickHitArea, 0);
    this.clickHitArea.zIndex = -1;

    this.input.setupClickToMove(this.clickHitArea, this.worldContainer);
    this.input.setupTouchJoystick(this.clickHitArea);
    this.uiLayer.addChild(this.input.getJoystickContainer());
  }

  // ─── Spawn ───

  private spawnAnimals() {
    for (let i = 0; i < MAX_ANIMALS; i++) {
      this.spawnOneAnimal();
    }
  }

  private spawnOneAnimal() {
    const hz = MAP.huntingZone;
    const x = hz.x + 30 + Math.random() * (hz.w - 60);
    const y = hz.y + 10 + Math.random() * (hz.h - 20);

    const container = new Container();

    const dir: Dir = 'down';
    const anim = createCharAnim(SPRITES.selvagem, dir, SPRITE_SCALE.monster);
    container.addChild(anim);

    const hpBar = new ProgressBar({
      width: 30, height: 4,
      fillColor: 0xff3333, bgColor: 0x333333,
      showText: false,
    });
    hpBar.setProgress(1);
    hpBar.x = -15;
    hpBar.y = -SIZES.animal - 14;
    container.addChild(hpBar);

    container.x = x;
    container.y = y;
    this.entityLayer.addChild(container);

    this.animals.push({
      gfx: container,
      anim,
      x, y,
      hp: ANIMAL_HP,
      maxHp: ANIMAL_HP,
      alive: true,
      respawnTimer: 0,
      hpBar,
      wanderTimer: 1 + Math.random() * 3,
      wanderDx: 0,
      wanderDy: 0,
      homeX: x,
      homeY: y,
      hitFlash: 0,
      claimed: false,
      dir,
    });
  }

  // ─── Monster Spawn ───

  private spawnMonsterForWave(wave: typeof MONSTER_WAVES[number]) {
    let mType: MonsterType = 'selvagem';
    let isElite = false;

    // Agressivo por chance da onda
    if (wave.agressivoChance > 0 && Math.random() < wave.agressivoChance) {
      mType = 'agressivo';
      this.agressivoCount++;

      // Voador substitui agressivo periodicamente
      if (wave.voador && this.agressivoCount % MONSTER_SPAWN.voadorEvery === 0) {
        mType = 'voador';
      }
    }

    // Elite (destruidor) por kills
    if (wave.elite && this.gs.monstrosMortos > 0 &&
        this.gs.monstrosMortos % MONSTER_SPAWN.eliteEveryKills === 0) {
      mType = 'destruidor';
      isElite = true;
    }

    this.spawnMonsterOfType(mType, isElite);
  }

  private spawnMonsterOfType(mType: MonsterType, isElite = false) {

    const stats = MONSTER_TYPES[mType];
    const hp = this.gs.getScaledMonsterHp(stats.hp);
    const dano = this.gs.getScaledMonsterDano(stats.dano);

    let x: number, y: number;
    const side = Math.floor(Math.random() * 4);
    switch (side) {
      case 0:
        x = 50 + Math.random() * (MAP.worldW - 100);
        y = MAP.monsterSpawn.north.y;
        break;
      case 1:
        x = 50 + Math.random() * (MAP.worldW - 100);
        y = MAP.monsterSpawn.south.y;
        break;
      case 2:
        x = MAP.monsterSpawn.east.x;
        y = 50 + Math.random() * (MAP.worldH - 100);
        break;
      default:
        x = MAP.monsterSpawn.west.x;
        y = 50 + Math.random() * (MAP.worldH - 100);
        break;
    }

    const container = new Container();
    const spritePath = MONSTER_SPRITE_MAP[mType];
    const dir: Dir = 'down';
    const row = dirToRow(dir);
    const frames = getCachedFrames(spritePath, row, true);
    const anim = new AnimatedSprite(frames);
    anim.anchor.set(0.5, 0.7);
    anim.scale.set(SPRITE_SCALE.monster);
    anim.animationSpeed = 0.12;
    anim.play();
    container.addChild(anim);

    const hpBar = new ProgressBar({
      width: 30, height: 4,
      fillColor: 0xff3333, bgColor: 0x333333,
      showText: false,
    });
    hpBar.setProgress(1);
    hpBar.x = -15;
    hpBar.y = -SIZES.monster - 14;
    container.addChild(hpBar);

    const typeLabel = new Text({
      text: mType,
      style: new TextStyle({ fontSize: 7, fill: '#ff8888' }),
    });
    typeLabel.anchor.set(0.5);
    typeLabel.y = 12;
    container.addChild(typeLabel);

    container.x = x;
    container.y = y;
    this.entityLayer.addChild(container);

    this.monsters.push({
      gfx: container,
      anim,
      x, y,
      hp,
      maxHp: hp,
      dano,
      monsterType: mType,
      hpBar,
      dir,
      alive: true,
      hitFlash: 0,
      attackTimer: 0,
      focaCerca: stats.focaCerca,
      ignoraCerca: stats.ignoraCerca,
      insideFence: false,
      isElite,
    });
  }

  // ─── Update ───

  update(dt: number) {
    if (this.gameOver) return;

    this.gs.updateScaling(dt);

    if (this.gs.isVictory()) {
      this.showGameOver(true);
      return;
    }
    if (this.gs.isDefeat()) {
      this.showGameOver(false);
      return;
    }

    // Wave change alert
    let waveIdx = -1;
    for (let i = 0; i < MONSTER_WAVES.length; i++) {
      if (this.gs.gameTimeSec >= MONSTER_WAVES[i].startSec) waveIdx = i;
    }
    if (waveIdx > this.lastWaveIndex) {
      this.lastWaveIndex = waveIdx;
      const waveNames = ['Onda 1: Selvagens!', 'Onda 2: Agressivos!', 'Onda 3: Voadores!',
                          'Onda 4: Elites!', 'Onda 5: Caos!', 'Onda 6: Inferno!'];
      if (waveIdx >= 0 && waveIdx < waveNames.length) {
        this.showWaveAlert(waveNames[waveIdx]);
      }
    }

    this.updateInput(dt);
    this.updatePlayer(dt);
    this.updateAnimals(dt);
    this.updateStations(dt);
    this.updateClients(dt);
    this.updateMonsters(dt);
    this.updateMonsterSpawn(dt);
    this.updateWorkers(dt);
    this.updateHireZones();
    this.updateUpgradeZones();
    this.updateDefenseUpgradeZones();
    this.updateShield(dt);
    this.updateGroundItems(dt);
    this.updateCoins(dt);
    this.updateFloatingTexts(dt);
    this.updateFenceVisuals();
    this.updateTowerVisuals();
    this.updateCamera();
    this.updateHUD();
    this.updateStationVisuals();
    this.gs.autoConvertCurrency();
  }

  private showGameOver(victory: boolean) {
    this.gameOver = true;
    this.gameOverGfx = new Container();
    this.gameOverGfx.zIndex = 200;

    const bg = new Graphics();
    bg.rect(0, 0, this.app.screen.width, this.app.screen.height).fill({ color: 0x000000, alpha: 0.7 });
    this.gameOverGfx.addChild(bg);

    const title = new Text({
      text: victory ? 'VITORIA!' : 'DERROTA!',
      style: new TextStyle({
        fontSize: 48,
        fill: victory ? '#44ff44' : '#ff4444',
        fontWeight: 'bold',
        fontFamily: 'Arial, sans-serif',
      }),
    });
    title.anchor.set(0.5);
    title.x = this.app.screen.width / 2;
    title.y = this.app.screen.height / 2 - 60;
    this.gameOverGfx.addChild(title);

    const elapsed = Math.floor(this.gs.gameTimeSec);
    const min = Math.floor(elapsed / 60);
    const sec = elapsed % 60;
    const timeStr = `${min}:${sec.toString().padStart(2, '0')}`;
    const sub = new Text({
      text: `Sobreviveu: ${timeStr}\nAbates: ${this.gs.abates} | Monstros mortos: ${this.gs.monstrosMortos}\nBronze: ${this.gs.bronze} | Prata: ${this.gs.prata} | Ouro: ${this.gs.ouro}`,
      style: new TextStyle({
        fontSize: 16,
        fill: '#ffffff',
        fontFamily: 'Arial, sans-serif',
        align: 'center',
      }),
    });
    sub.anchor.set(0.5);
    sub.x = this.app.screen.width / 2;
    sub.y = this.app.screen.height / 2 + 10;
    this.gameOverGfx.addChild(sub);

    // Restart button
    const btnW = 200;
    const btnH = 44;
    const btnX = this.app.screen.width / 2;
    const btnY = this.app.screen.height / 2 + 80;

    const btnBg = new Graphics();
    btnBg.roundRect(-btnW / 2, -btnH / 2, btnW, btnH, 8).fill({ color: victory ? 0x228822 : 0x882222 });
    btnBg.roundRect(-btnW / 2, -btnH / 2, btnW, btnH, 8).stroke({ color: 0xffffff, width: 2 });
    btnBg.x = btnX;
    btnBg.y = btnY;
    btnBg.eventMode = 'static';
    btnBg.cursor = 'pointer';
    btnBg.on('pointerdown', () => {
      // Clear save and reload
      indexedDB.deleteDatabase('universe-axiom-games');
      setTimeout(() => window.location.reload(), 300);
    });
    this.gameOverGfx.addChild(btnBg);

    const btnText = new Text({
      text: 'Jogar Novamente',
      style: new TextStyle({
        fontSize: 18,
        fill: '#ffffff',
        fontWeight: 'bold',
        fontFamily: 'Arial, sans-serif',
      }),
    });
    btnText.anchor.set(0.5);
    btnText.x = btnX;
    btnText.y = btnY;
    this.gameOverGfx.addChild(btnText);

    this.uiLayer.addChild(this.gameOverGfx);
  }

  private showWaveAlert(message: string) {
    const alert = new Container();
    alert.zIndex = 150;

    const bg = new Graphics();
    bg.roundRect(this.app.screen.width / 2 - 120, this.app.screen.height / 2 - 30, 240, 50, 8)
      .fill({ color: 0x880000, alpha: 0.8 });
    bg.roundRect(this.app.screen.width / 2 - 120, this.app.screen.height / 2 - 30, 240, 50, 8)
      .stroke({ color: 0xff4444, width: 2 });
    alert.addChild(bg);

    const text = new Text({
      text: message,
      style: new TextStyle({
        fontSize: 20,
        fill: '#ff4444',
        fontWeight: 'bold',
        fontFamily: 'Arial, sans-serif',
      }),
    });
    text.anchor.set(0.5);
    text.x = this.app.screen.width / 2;
    text.y = this.app.screen.height / 2 - 5;
    alert.addChild(text);

    this.uiLayer.addChild(alert);

    // Fade out after 3 seconds
    let timer = 3;
    const fadeInterval = setInterval(() => {
      timer -= 0.05;
      if (timer <= 1) {
        alert.alpha = timer;
      }
      if (timer <= 0) {
        this.uiLayer.removeChild(alert);
        clearInterval(fadeInterval);
      }
    }, 50);
  }

  private formatTime(totalSec: number): string {
    const remaining = Math.max(0, GAME_DURATION - totalSec);
    const min = Math.floor(remaining / 60);
    const sec = Math.floor(remaining % 60);
    return `${min}:${sec.toString().padStart(2, '0')}`;
  }

  private updateInput(_dt: number) {
    this.input.update(this.playerX, this.playerY);
  }

  private updatePlayer(dt: number) {
    // Player respawn
    if (this.playerDead) {
      this.playerRespawnTimer -= dt;
      this.playerGfx.alpha = 0.3 + Math.sin(performance.now() / 200) * 0.2;
      if (this.playerRespawnTimer <= 0) {
        this.playerDead = false;
        this.gs.respawnPlayer();
        this.playerX = MAP.playerSpawn.x;
        this.playerY = MAP.playerSpawn.y;
        this.playerGfx.alpha = 1;
        this.spawnFloatingText(this.playerX, this.playerY - 30, 'Reviveu!', '#44ff44');
      }
      this.playerGfx.x = this.playerX;
      this.playerGfx.y = this.playerY;
      this.updatePlayerHpBar();
      return;
    }

    // Check if player died
    if (this.gs.isPlayerDead()) {
      this.playerDead = true;
      this.playerRespawnTimer = 3; // 3 seconds respawn
      this.playerAnim.gotoAndStop(0);
      this.spawnFloatingText(this.playerX, this.playerY - 30, 'Morreu!', '#ff0000');
      return;
    }

    const state = this.input.getState();

    const wasMoving = this.playerMoving;
    this.playerMoving = state.moving;

    if (state.moving) {
      this.playerX += state.dx * PLAYER_SPEED * dt;
      this.playerY += state.dy * PLAYER_SPEED * dt;

      this.playerX = Math.max(SIZES.player, Math.min(MAP.worldW - SIZES.player, this.playerX));
      this.playerY = Math.max(SIZES.player, Math.min(MAP.worldH - SIZES.player, this.playerY));

      if (Math.abs(state.dx) > Math.abs(state.dy)) {
        this.playerDir = state.dx > 0 ? 'right' : 'left';
      } else {
        this.playerDir = state.dy > 0 ? 'down' : 'up';
      }

      if (this.playerDir !== this.lastPlayerDir) {
        this.lastPlayerDir = this.playerDir;
        updateCharDir(this.playerAnim, SPRITES.player, this.playerDir);
      }

      if (!this.playerAnim.playing) this.playerAnim.play();
    } else if (wasMoving) {
      this.playerAnim.gotoAndStop(0);
    }

    this.playerGfx.x = this.playerX;
    this.playerGfx.y = this.playerY;

    if (this.attackCooldown > 0) this.attackCooldown -= dt;

    this.checkProximityInteractions(dt);
    this.updatePlayerHpBar();
  }

  private updatePlayerHpBar() {
    const pct = this.gs.playerHp / this.gs.playerHpMax;
    this.playerHpBar.setProgress(pct);
    if (pct > 0.5) {
      this.playerHpBar.setFillColor(0x44cc44);
    } else if (pct > 0.25) {
      this.playerHpBar.setFillColor(0xcccc44);
    } else {
      this.playerHpBar.setFillColor(0xcc4444);
    }
  }

  private checkProximityInteractions(dt: number) {
    // 1. Pegar moedas
    for (let i = this.coins.length - 1; i >= 0; i--) {
      const c = this.coins[i];
      if (this.dist(this.playerX, this.playerY, c.x, c.y) < PROXIMITY_MONEY) {
        this.gs.bronze += CARNE_SELL_VALUE;
        this.spawnFloatingText(c.x, c.y, `+${CARNE_SELL_VALUE}B`, '#ffd700');
        this.itemLayer.removeChild(c.gfx);
        this.coins.splice(i, 1);
      }
    }

    // 2. Depositar carne crua na mesa do assador (area ampla — toda a mesa)
    if (this.gs.playerInventoryType === 'carne_crua' && this.gs.playerInventoryCount > 0) {
      if (this.dist(this.playerX, this.playerY, MAP.assador.x, MAP.assador.y) < 80) {
        const dropped = this.gs.playerDropAll();
        this.gs.depositCarneNaMesa(dropped.count);
        this.spawnFloatingText(MAP.assador.x, MAP.assador.y - 30, `+${dropped.count} Crua`, '#ff6666');
      }
    }

    // 3. Depositar carne assada no balcao
    if (this.gs.playerInventoryType === 'carne_assada' && this.gs.playerInventoryCount > 0) {
      if (this.dist(this.playerX, this.playerY, MAP.balcaoVenda.x, MAP.balcaoVenda.y) < 60) {
        const dropped = this.gs.playerDropAll();
        this.gs.depositCarneNoBalcao(dropped.count);
        this.spawnFloatingText(MAP.balcaoVenda.x, MAP.balcaoVenda.y - 20, `+${dropped.count} Assada`, '#ffaa00');
      }
    }

    // 4. Pegar carne assada da bancada (area ampla — toda a mesa)
    if (this.gs.bancadaCarne > 0 && !this.gs.isPlayerInventoryFull()) {
      if (this.gs.playerInventoryType === 'none' || this.gs.playerInventoryType === 'carne_assada') {
        if (this.dist(this.playerX, this.playerY, MAP.assador.x, MAP.assador.y) < 80) {
          const space = this.gs.bagPlayer - this.gs.playerInventoryCount;
          const toPickup = Math.min(this.gs.bancadaCarne, space);
          this.gs.bancadaCarne -= toPickup;
          this.gs.playerPickup('carne_assada', toPickup);
          this.spawnFloatingText(MAP.assador.x, MAP.assador.y - 30, `Pegou ${toPickup} Assada`, '#ffaa00');
        }
      }
    }

    // 5. Auto-ataque em animais E monstros
    if (this.attackCooldown <= 0) {
      let attacked = false;

      // Prioridade: monstros primeiro (defesa)
      for (const m of this.monsters) {
        if (!m.alive) continue;
        if (this.dist(this.playerX, this.playerY, m.x, m.y) < PROXIMITY_ATTACK) {
          this.attackMonster(m);
          attacked = true;
          break;
        }
      }

      // Depois animais (caca)
      if (!attacked) {
        for (const a of this.animals) {
          if (!a.alive) continue;
          if (this.dist(this.playerX, this.playerY, a.x, a.y) < PROXIMITY_ATTACK) {
            this.attackAnimal(a);
            break;
          }
        }
      }
    }

    // 6. Reparo da cerca (perto do portao, com cooldown de 1.5s)
    if (this.repairCooldown > 0) this.repairCooldown -= dt;
    if (this.repairCooldown <= 0 && this.dist(this.playerX, this.playerY, MAP.fence.gateX, MAP.fence.gateY) < 50) {
      if (this.gs.cercaHp < this.gs.cercaHpMax && this.gs.repairCerca()) {
        this.spawnFloatingText(MAP.fence.gateX, MAP.fence.gateY - 20, `+${FENCE.reparoHp} Cerca (-${FENCE.reparoCustoBronze}B)`, '#44ff44');
        this.repairCooldown = 1.5;
      }
    }

    // 7. Construir torre (perto de torre vazia)
    for (let i = this.gs.torreCount; i < MAP.towers.length; i++) {
      const tp = MAP.towers[i];
      if (this.dist(this.playerX, this.playerY, tp.x, tp.y) < 40) {
        if (this.gs.buildTorre()) {
          this.spawnFloatingText(tp.x, tp.y - 30, `Torre construida! (-${TOWER.custoPrata}P)`, '#44ff44');
        }
        break;
      }
    }

    // 8. Pegar itens do chao (com feedback quando nao pode)
    if (this.pickupFeedbackCooldown > 0) this.pickupFeedbackCooldown -= dt;
    for (let i = this.groundItems.length - 1; i >= 0; i--) {
      const item = this.groundItems[i];
      if (this.dist(this.playerX, this.playerY, item.x, item.y) < PROXIMITY_PICKUP) {
        if (this.gs.isPlayerInventoryFull()) {
          if (this.pickupFeedbackCooldown <= 0) {
            this.spawnFloatingText(this.playerX, this.playerY - 30, 'Bag cheia!', '#ff4444');
            this.pickupFeedbackCooldown = 1.5;
          }
        } else if (this.gs.playerInventoryType !== 'none' && this.gs.playerInventoryType !== item.type) {
          if (this.pickupFeedbackCooldown <= 0) {
            this.spawnFloatingText(this.playerX, this.playerY - 30, 'Tipo diferente!', '#ff8844');
            this.pickupFeedbackCooldown = 1.5;
          }
        } else {
          this.gs.playerPickup(item.type, 1);
          const label = item.type === 'carne_crua' ? 'Carne Crua' : 'Carne Assada';
          this.spawnFloatingText(item.x, item.y, `+1 ${label}`, '#ff6666');
          this.itemLayer.removeChild(item.gfx);
          this.groundItems.splice(i, 1);
        }
        break; // só interage com 1 item por frame
      }
    }
  }

  private attackAnimal(animal: Animal, setPlayerCooldown = true) {
    animal.hp -= ATTACK_DAMAGE;
    animal.hitFlash = 0.15;
    if (setPlayerCooldown) this.attackCooldown = ATTACK_COOLDOWN;

    animal.anim.tint = 0xff0000;
    animal.hpBar.setProgress(animal.hp / animal.maxHp);

    if (animal.hp <= 0) {
      this.killAnimal(animal);
    }
  }

  private attackMonster(m: Monster) {
    m.hp -= HERO_STATS.dano;
    m.hitFlash = 0.15;
    m.anim.tint = 0xff0000;
    m.hpBar.setProgress(Math.max(0, m.hp / m.maxHp));
    this.attackCooldown = ATTACK_COOLDOWN;

    this.spawnFloatingText(m.x, m.y - 20, `-${HERO_STATS.dano}`, '#ff8844');

    if (m.hp <= 0) {
      this.onMonsterKilled(m);
    }
  }

  /** Monstro morreu: incrementa contador, dropa loot bronze, floating text */
  private onMonsterKilled(m: Monster) {
    m.alive = false;
    this.gs.monstrosMortos++;

    // Loot: bronze baseado no tipo
    const lootKey = m.isElite ? 'elite' : m.monsterType;
    const loot = MONSTER_LOOT[lootKey] || 5;
    this.gs.bronze += loot;
    this.spawnFloatingText(m.x, m.y - 30, `+${loot}B`, '#ffd700');
    this.spawnFloatingText(m.x, m.y - 15, 'Morto!', '#44ff44');
  }

  private killAnimal(animal: Animal) {
    animal.alive = false;
    animal.gfx.visible = false;
    animal.respawnTimer = ANIMAL_RESPAWN;
    this.gs.abates++;

    this.spawnGroundItem(animal.x, animal.y, 'carne_crua');
    this.spawnFloatingText(animal.x, animal.y - 20, 'Abatido!', '#ff4444');
  }

  private spawnGroundItem(x: number, y: number, type: InventoryType) {
    const texPath = type === 'carne_crua' ? SPRITES.meat : SPRITES.yakitori;
    const tex = Texture.from(texPath);
    const spr = new Sprite(tex);
    spr.anchor.set(0.5);
    spr.scale.set(SPRITE_SCALE.item);
    spr.x = x + (Math.random() - 0.5) * 20;
    spr.y = y + (Math.random() - 0.5) * 20;
    this.itemLayer.addChild(spr);

    this.groundItems.push({
      gfx: spr,
      x: spr.x,
      y: spr.y,
      type,
      bobTimer: Math.random() * Math.PI * 2,
      claimed: false,
    });
  }

  private spawnCoin(x: number, y: number) {
    const tex = Texture.from(SPRITES.goldcoin);
    const spr = new Sprite(tex);
    spr.anchor.set(0.5);
    spr.scale.set(SPRITE_SCALE.coin);
    spr.x = x + (Math.random() - 0.5) * 30;
    spr.y = y + (Math.random() - 0.5) * 10;
    this.itemLayer.addChild(spr);

    this.coins.push({
      gfx: spr,
      x: spr.x,
      y: spr.y,
      bobTimer: Math.random() * Math.PI * 2,
      claimed: false,
    });
  }

  // ─── Update Systems ───

  private updateAnimals(dt: number) {
    const hz = MAP.huntingZone;
    for (const a of this.animals) {
      if (!a.alive) {
        a.respawnTimer -= dt;
        if (a.respawnTimer <= 0) {
          a.alive = true;
          a.hp = a.maxHp;
          a.claimed = false;
          a.hpBar.setProgress(1);
          a.x = a.homeX + (Math.random() - 0.5) * 60;
          a.y = a.homeY + (Math.random() - 0.5) * 40;
          a.x = Math.max(hz.x + 10, Math.min(hz.x + hz.w - 10, a.x));
          a.y = Math.max(hz.y + 5, Math.min(hz.y + hz.h - 5, a.y));
          a.gfx.visible = true;
          a.gfx.x = a.x;
          a.gfx.y = a.y;
        }
        continue;
      }

      if (a.hitFlash > 0) {
        a.hitFlash -= dt;
        if (a.hitFlash <= 0) {
          a.anim.tint = 0xffffff;
        }
      }

      a.wanderTimer -= dt;
      if (a.wanderTimer <= 0) {
        a.wanderTimer = 2 + Math.random() * 3;
        const angle = Math.random() * Math.PI * 2;
        a.wanderDx = Math.cos(angle) * 30;
        a.wanderDy = Math.sin(angle) * 30;

        const newDir: Dir = Math.abs(a.wanderDx) > Math.abs(a.wanderDy)
          ? (a.wanderDx > 0 ? 'right' : 'left')
          : (a.wanderDy > 0 ? 'down' : 'up');
        if (newDir !== a.dir) {
          a.dir = newDir;
          updateCharDir(a.anim, SPRITES.selvagem, a.dir);
        }
      }

      const animalSpeed = 20;
      const mag = Math.max(1, Math.abs(a.wanderDx) + Math.abs(a.wanderDy));
      a.x += a.wanderDx / mag * animalSpeed * dt;
      a.y += a.wanderDy / mag * animalSpeed * dt;

      a.x = Math.max(hz.x + 10, Math.min(hz.x + hz.w - 10, a.x));
      a.y = Math.max(hz.y + 5, Math.min(hz.y + hz.h - 5, a.y));

      a.gfx.x = a.x;
      a.gfx.y = a.y;
    }
  }

  private updateStations(dt: number) {
    this.gs.updateFogueira(dt);

    this.fogueiraBar.visible = this.gs.fogueiraCarne > 0;
    if (this.gs.fogueiraCarne > 0) {
      this.fogueiraBar.setProgress(1 - this.gs.fogueiraTimer / COOK_TIME);
    }

    if (this.assadorFlames) {
      this.assadorFlames.scale.x = 0.9 + Math.sin(performance.now() / 200) * 0.1;
      this.assadorFlames.scale.y = 0.9 + Math.cos(performance.now() / 150) * 0.15;
    }
  }

  private getClientSpawnInterval(): number {
    let interval = CLIENT_SPAWN_BASE_INTERVAL;
    if (this.gs.workerCacador > 0) interval *= 0.75;
    if (this.gs.workerGarcom > 0) interval *= 0.75;
    return interval;
  }

  private updateClients(dt: number) {
    const hasFood = this.gs.balcaoVenda > 0;

    const waitingCount = this.clients.filter(c => c.state === 'walking' || c.state === 'waiting').length;
    this.clientSpawnTimer -= dt;
    if (this.clientSpawnTimer <= 0 && waitingCount < MAX_CLIENTS_QUEUE && hasFood) {
      this.clientSpawnTimer = this.getClientSpawnInterval();
      this.spawnClient(waitingCount);
    }

    // Servir clientes (auto sell)
    for (const c of this.clients) {
      if (c.state !== 'waiting' || c.claimedByGarcom) continue;
      if (this.gs.balcaoVenda <= 0) break;

      const value = this.gs.sellCarne();
      if (value > 0) {
        c.served = true;
        c.state = 'eating';
        c.timer = CLIENT_EAT_TIME;
        c.anim.tint = 0xffffff;
        this.spawnFloatingText(c.x, c.y - 20, 'Comprando!', '#daa520');
      }
    }

    const clientSpeed = 80;
    for (let i = this.clients.length - 1; i >= 0; i--) {
      const c = this.clients[i];

      switch (c.state) {
        case 'walking': {
          const dx = c.targetX - c.x;
          if (Math.abs(dx) > 5) {
            c.x += Math.sign(dx) * clientSpeed * dt;
          } else {
            c.x = c.targetX;
            c.state = 'waiting';
            c.anim.gotoAndStop(0);
          }
          break;
        }

        case 'waiting': {
          c.patience -= dt;
          c.patienceBar.setProgress(Math.max(0, c.patience / c.maxPatience));

          const pctPatience = c.patience / c.maxPatience;
          if (pctPatience < 0.3) {
            c.anim.tint = 0xff4444;
          } else if (pctPatience < 0.6) {
            c.anim.tint = 0xffaa44;
          } else {
            c.anim.tint = 0xffffff;
          }

          if (c.patience <= 0) {
            c.served = false;
            c.state = 'leaving';
            c.anim.tint = 0xff0000;
            this.gs.registerClientePerdido();
            this.spawnFloatingText(c.x, c.y - 20, 'Desistiu!', '#ff4444');
            c.patienceBar.visible = false;
          }
          break;
        }

        case 'eating':
          c.timer -= dt;
          if (c.timer <= 0) {
            const satisfacao = Math.round((c.patience / c.maxPatience) * 10);
            this.gs.registerClienteAtendido(satisfacao);

            this.spawnCoin(MAP.clientZone.x, MAP.clientZone.y);

            const stars = satisfacao >= 8 ? '***' : satisfacao >= 5 ? '**' : '*';
            this.spawnFloatingText(c.x, c.y - 20, `Vendeu! ${stars}`, '#ffd700');
            c.state = 'leaving';
            c.patienceBar.visible = false;

            updateCharDir(c.anim, CLIENT_SPRITES[0], 'right');
            c.anim.play();
          }
          break;

        case 'leaving':
          c.x += 100 * dt;
          if (c.x > MAP.worldW + 50) {
            this.entityLayer.removeChild(c.gfx);
            this.clients.splice(i, 1);
            this.reindexClientQueue();
          }
          break;
      }

      c.gfx.x = c.x;
      c.gfx.y = c.y;
    }
  }

  private reindexClientQueue() {
    let idx = 0;
    for (const c of this.clients) {
      if (c.state === 'walking' || c.state === 'waiting') {
        c.queueIndex = idx;
        c.targetX = MAP.clientZone.x - 60 + idx * 25;
        idx++;
      }
    }
  }

  private spawnClient(queueIndex: number) {
    const y = MAP.clientZone.y;
    const x = MAP.worldW + 20;
    const targetX = MAP.clientZone.x - 60 + queueIndex * 25;
    const patience = CLIENT_PATIENCE;

    const container = new Container();

    const spritePath = CLIENT_SPRITES[Math.floor(Math.random() * CLIENT_SPRITES.length)];
    const anim = createCharAnim(spritePath, 'left', SPRITE_SCALE.character);
    container.addChild(anim);

    const clientLabel = new Text({
      text: 'Cliente',
      style: new TextStyle({ fontSize: 9, fill: '#ffffff' }),
    });
    clientLabel.anchor.set(0.5);
    clientLabel.y = -SIZES.client - 20;
    container.addChild(clientLabel);

    const patienceBar = new ProgressBar({
      width: 24, height: 3,
      fillColor: 0x44ff44, bgColor: 0x333333,
      showText: false,
    });
    patienceBar.x = -12;
    patienceBar.y = -SIZES.client - 12;
    container.addChild(patienceBar);

    container.x = x;
    container.y = y;
    this.entityLayer.addChild(container);

    this.clients.push({
      gfx: container,
      anim,
      x, y,
      state: 'walking',
      timer: 0,
      targetX,
      patience,
      maxPatience: patience,
      served: false,
      queueIndex,
      patienceBar,
      claimedByGarcom: false,
    });
  }

  // ─── Monster Update ───

  private getCurrentWave(): typeof MONSTER_WAVES[number] | null {
    let current: typeof MONSTER_WAVES[number] | null = null;
    for (const wave of MONSTER_WAVES) {
      if (this.gs.gameTimeSec >= wave.startSec) {
        current = wave;
      }
    }
    return current;
  }

  private updateMonsterSpawn(dt: number) {
    const wave = this.getCurrentWave();
    if (!wave) return; // Ainda nao comecou (primeiros 30s)

    this.monsterSpawnTimer -= dt;
    if (this.monsterSpawnTimer <= 0) {
      // Scaling acelera o spawn levemente
      const scalingBoost = 1 + this.gs.scalingLevel * 0.03;
      this.monsterSpawnTimer = wave.intervalSec / scalingBoost;
      this.spawnMonsterForWave(wave);
    }
  }

  private updateMonsters(dt: number) {
    const f = MAP.fence;
    const gateLeft = f.gateX - f.gateW / 2;
    const gateRight = f.gateX + f.gateW / 2;

    for (let i = this.monsters.length - 1; i >= 0; i--) {
      const m = this.monsters[i];
      if (!m.alive) {
        this.entityLayer.removeChild(m.gfx);
        this.monsters.splice(i, 1);
        continue;
      }

      // Hit flash recovery
      if (m.hitFlash > 0) {
        m.hitFlash -= dt;
        if (m.hitFlash <= 0) {
          m.anim.tint = 0xffffff;
        }
      }

      // ─── Choose target ───
      let targetX: number;
      let targetY: number;

      if (m.insideFence) {
        // Inside fence: attack player
        targetX = this.playerX;
        targetY = this.playerY;
      } else if (m.focaCerca) {
        // Destruidor: head straight for nearest fence wall
        targetX = Math.max(f.x, Math.min(f.x + f.w, m.x));
        targetY = Math.max(f.y, Math.min(f.y + f.h, m.y));
      } else {
        // Default: head for the gate
        targetX = f.gateX;
        targetY = f.gateY;
      }

      // ─── Movement ───
      const dx = targetX - m.x;
      const dy = targetY - m.y;
      const distToTarget = Math.sqrt(dx * dx + dy * dy);
      const speed = 40;

      const prevX = m.x;
      const prevY = m.y;

      if (distToTarget > 15) {
        m.x += (dx / distToTarget) * speed * dt;
        m.y += (dy / distToTarget) * speed * dt;

        const newDir: Dir = Math.abs(dx) > Math.abs(dy)
          ? (dx > 0 ? 'right' : 'left')
          : (dy > 0 ? 'down' : 'up');
        if (newDir !== m.dir) {
          m.dir = newDir;
          const spritePath = MONSTER_SPRITE_MAP[m.monsterType];
          const row = dirToRow(m.dir);
          const frames = getCachedFrames(spritePath, row, true);
          if (m.anim.textures !== frames) {
            m.anim.textures = frames;
            m.anim.play();
          }
        }
      }

      // ─── Fence collision ───
      if (this.gs.cercaHp > 0 && !m.ignoraCerca && !m.insideFence) {
        const insideNow = m.x > f.x + 4 && m.x < f.x + f.w - 4 &&
                          m.y > f.y + 4 && m.y < f.y + f.h - 4;

        if (insideNow) {
          // Check if entering through the gate
          const throughGate = m.x >= gateLeft && m.x <= gateRight &&
                             prevY >= f.y + f.h - 10;

          if (throughGate) {
            m.insideFence = true; // Entered through gate!
          } else {
            // Blocked by fence wall — push back
            m.x = prevX;
            m.y = prevY;
          }
        }
      }

      // When fence destroyed, all monsters enter freely
      if (this.gs.cercaHp <= 0 && !m.ignoraCerca && !m.insideFence) {
        const insideNow = m.x > f.x && m.x < f.x + f.w &&
                          m.y > f.y && m.y < f.y + f.h;
        if (insideNow) m.insideFence = true;
      }

      // Voadores fly over fence — always considered "inside"
      if (m.ignoraCerca) {
        const insideNow = m.x > f.x && m.x < f.x + f.w &&
                          m.y > f.y && m.y < f.y + f.h;
        if (insideNow) m.insideFence = true;
      }

      // ─── Attacks ───

      // Monster at fence wall: attack fence
      if (!m.insideFence && !m.ignoraCerca && this.gs.cercaHp > 0) {
        const nearWall = this.isNearFenceBorder(m.x, m.y, 12);
        if (nearWall) {
          m.attackTimer -= dt;
          if (m.attackTimer <= 0) {
            m.attackTimer = 1.0;
            this.gs.cercaHp = Math.max(0, this.gs.cercaHp - m.dano);
            this.spawnFloatingText(m.x, m.y - 20, `-${m.dano} Cerca`, '#ff4444');
          }
          // Stop movement while attacking fence
          m.x = prevX;
          m.y = prevY;
        }
      }

      // Voador attacks cerca from inside
      if (m.ignoraCerca && m.insideFence && this.gs.cercaHp > 0) {
        m.attackTimer -= dt;
        if (m.attackTimer <= 0) {
          m.attackTimer = 1.5;
          this.gs.cercaHp = Math.max(0, this.gs.cercaHp - m.dano);
          this.spawnFloatingText(m.x, m.y - 20, `-${m.dano} Cerca`, '#ff4444');
        }
      }

      // Monster inside fence: attack player if close
      if (m.insideFence && !this.playerDead) {
        const distToPlayer = this.dist(m.x, m.y, this.playerX, this.playerY);
        if (distToPlayer < 25) {
          m.attackTimer -= dt;
          if (m.attackTimer <= 0) {
            m.attackTimer = 1.0;
            this.gs.playerTakeDamage(m.dano);
            this.spawnFloatingText(this.playerX, this.playerY - 30, `-${m.dano} HP`, '#ff4444');
          }
        }
      }

      // ─── Tower DPS ───
      for (let t = 0; t < this.gs.torreCount && t < MAP.towers.length; t++) {
        const tower = MAP.towers[t];
        if (this.dist(m.x, m.y, tower.x, tower.y) < 150) {
          m.hp -= TOWER.dano * dt;
          m.hpBar.setProgress(Math.max(0, m.hp / m.maxHp));
        }
      }

      // ─── Death check ───
      if (m.hp <= 0) {
        this.onMonsterKilled(m);
      }

      m.gfx.x = m.x;
      m.gfx.y = m.y;
    }
  }

  private isNearFenceBorder(x: number, y: number, threshold: number): boolean {
    const f = MAP.fence;
    if (y >= f.y - threshold && y <= f.y + threshold && x >= f.x && x <= f.x + f.w) return true;
    if (y >= f.y + f.h - threshold && y <= f.y + f.h + threshold && x >= f.x && x <= f.x + f.w) {
      const gateLeft = f.gateX - f.gateW / 2;
      const gateRight = f.gateX + f.gateW / 2;
      if (x < gateLeft || x > gateRight) return true;
    }
    if (x >= f.x - threshold && x <= f.x + threshold && y >= f.y && y <= f.y + f.h) return true;
    if (x >= f.x + f.w - threshold && x <= f.x + f.w + threshold && y >= f.y && y <= f.y + f.h) return true;
    return false;
  }

  private updateFenceVisuals() {
    this.drawFence();
    const pct = this.gs.cercaHp / this.gs.cercaHpMax;
    this.fenceHpBar.setProgress(pct);
    if (pct > 0.5) {
      this.fenceHpBar.setFillColor(0x44cc44);
    } else if (pct > 0.25) {
      this.fenceHpBar.setFillColor(0xcccc44);
    } else {
      this.fenceHpBar.setFillColor(0xcc4444);
    }
    this.fenceHpText.text = `Cerca: ${Math.floor(this.gs.cercaHp)}/${this.gs.cercaHpMax}`;
  }

  private updateTowerVisuals() {
    for (let i = 0; i < this.towerGfxList.length; i++) {
      const built = i < this.gs.torreCount;
      this.towerGfxList[i].alpha = built ? 1.0 : 0.4;
      // Hide cost label when built (last child is costLabel)
      const children = this.towerGfxList[i].children;
      if (children.length >= 3) {
        children[children.length - 1].visible = !built;
      }
    }
  }

  private updateGroundItems(dt: number) {
    for (const item of this.groundItems) {
      item.bobTimer += dt * 3;
      item.gfx.y = item.y + Math.sin(item.bobTimer) * 2;
    }
  }

  private updateCoins(dt: number) {
    for (const coin of this.coins) {
      coin.bobTimer += dt * 4;
      coin.gfx.y = coin.y + Math.sin(coin.bobTimer) * 3;
    }
  }

  private updateFloatingTexts(dt: number) {
    for (let i = this.floatingTexts.length - 1; i >= 0; i--) {
      const ft = this.floatingTexts[i];
      ft.timer -= dt;
      ft.text.y -= ft.vy * dt;
      ft.text.alpha = Math.max(0, ft.timer / 1.5);

      if (ft.timer <= 0) {
        this.worldContainer.removeChild(ft.text);
        this.floatingTexts.splice(i, 1);
      }
    }
  }

  private spawnFloatingText(x: number, y: number, message: string, color: string) {
    const text = new Text({
      text: message,
      style: new TextStyle({
        fontSize: 14,
        fill: color,
        fontWeight: 'bold',
        dropShadow: { color: '#000000', blur: 2, distance: 1 } as any,
      }),
    });
    text.anchor.set(0.5);
    text.x = x;
    text.y = y;
    text.zIndex = 50;
    this.worldContainer.addChild(text);

    this.floatingTexts.push({ text, timer: 1.5, vy: 40 });
  }

  private updateCamera() {
    this.camera.setScreenSize(this.app.screen.width, this.app.screen.height);
    this.camera.follow(this.playerX, this.playerY, 60);
  }

  private updateHUD() {
    this.hudMoney.text = `Bronze: ${this.gs.bronze}`;

    if (this.gs.prata > 0 || this.gs.ouro > 0) {
      let txt = `Prata: ${this.gs.prata}`;
      if (this.gs.ouro > 0) txt += ` | Ouro: ${this.gs.ouro}`;
      this.hudPrata.text = txt;
    } else {
      this.hudPrata.text = '';
    }

    if (this.gs.playerInventoryType === 'none' || this.gs.playerInventoryCount === 0) {
      this.hudBackpack.text = 'Mochila: vazia';
      this.playerCarryText.text = '';
    } else {
      const typeName = this.gs.playerInventoryType === 'carne_crua' ? 'Carne Crua' : 'Carne Assada';
      this.hudBackpack.text = `Mochila: ${typeName} x${this.gs.playerInventoryCount}`;
      this.playerCarryText.text = `${typeName} x${this.gs.playerInventoryCount}`;
    }

    const activeMonsters = this.monsters.filter(m => m.alive).length;
    this.hudAbates.text = `Abates: ${this.gs.abates} | Monstros: ${activeMonsters} (${this.gs.monstrosMortos} mortos)`;

    this.hudTimer.x = this.app.screen.width / 2;
    this.hudTimer.text = this.formatTime(this.gs.gameTimeSec);

    // Timer color: green > 10min, yellow > 5min, red <= 5min
    const remaining = GAME_DURATION - this.gs.gameTimeSec;
    if (remaining <= 300) {
      this.hudTimer.style.fill = '#ff4444';
    } else if (remaining <= 600) {
      this.hudTimer.style.fill = '#ffcc44';
    } else {
      this.hudTimer.style.fill = '#44ff44';
    }

    this.hudCerca.text = `Cerca: ${Math.floor(this.gs.cercaHp)}/${this.gs.cercaHpMax} | Torres: ${this.gs.torreCount} | HP: ${Math.floor(this.gs.playerHp)}/${this.gs.playerHpMax}`;

    const wTotal = this.gs.workerCacador + this.gs.workerAjudante + this.gs.workerGarcom + this.gs.workerContador + this.gs.workerArqueiro;
    if (wTotal > 0) {
      const parts: string[] = [];
      if (this.gs.workerCacador > 0) parts.push(`Cac:${this.gs.workerCacador}`);
      if (this.gs.workerAjudante > 0) parts.push(`Aju:${this.gs.workerAjudante}`);
      if (this.gs.workerGarcom > 0) parts.push(`Gar:${this.gs.workerGarcom}`);
      if (this.gs.workerContador > 0) parts.push(`Con:${this.gs.workerContador}`);
      if (this.gs.workerArqueiro > 0) parts.push(`Arq:${this.gs.workerArqueiro}`);
      this.hudWorkers.text = `Workers: ${parts.join(' ')}`;
    } else {
      this.hudWorkers.text = '';
    }
  }

  private updateStationVisuals() {
    this.mesaCarneText.text = this.gs.depositoCarne > 0 ? `Crua: ${this.gs.depositoCarne}` : '';
    this.bancadaCarneText.text = this.gs.bancadaCarne > 0 ? `Assada: ${this.gs.bancadaCarne}` : '';
    this.balcaoCarneText.text = this.gs.balcaoVenda > 0 ? `Venda: ${this.gs.balcaoVenda}` : '';
  }

  // ─── Upgrade Zones (Bag) ───

  private buildUpgradeZones() {
    const upgradeY = MAP.fence.y + 20;
    const configs: { upgradeType: 'player' | 'ajudante' | 'garcom' | 'contador'; x: number; y: number; color: number; labelStr: string }[] = [
      { upgradeType: 'player',   x: 560, y: upgradeY, color: COLORS.player,         labelStr: 'Bag Caua' },
      { upgradeType: 'ajudante', x: 630, y: upgradeY, color: COLORS.workerAjudante, labelStr: 'Bag Aju' },
      { upgradeType: 'garcom',   x: 700, y: upgradeY, color: COLORS.workerGarcom,   labelStr: 'Bag Gar' },
      { upgradeType: 'contador', x: 770, y: upgradeY, color: COLORS.workerContador, labelStr: 'Bag Con' },
    ];

    for (const cfg of configs) {
      const container = new Container();
      container.x = cfg.x;
      container.y = cfg.y;

      const bg = new Graphics();
      bg.roundRect(-20, -16, 40, 32, 4).fill({ color: cfg.color, alpha: 0.3 });
      bg.stroke({ color: cfg.color, width: 1 });
      container.addChild(bg);

      const labelText = new Text({
        text: cfg.labelStr,
        style: new TextStyle({ fontSize: 8, fill: '#ffffff', fontWeight: 'bold' }),
      });
      labelText.anchor.set(0.5);
      labelText.y = 22;
      container.addChild(labelText);

      const priceText = new Text({
        text: `${BAG_UPGRADE_COST}B`,
        style: new TextStyle({ fontSize: 8, fill: '#ffcc00' }),
      });
      priceText.anchor.set(0.5);
      priceText.y = 32;
      container.addChild(priceText);

      const bagSize = cfg.upgradeType === 'player' ? this.gs.bagPlayer
        : cfg.upgradeType === 'ajudante' ? this.gs.bagAjudante
        : cfg.upgradeType === 'garcom' ? this.gs.bagGarcom : this.gs.bagContador;
      const capacityText = new Text({
        text: `${bagSize}`,
        style: new TextStyle({ fontSize: 9, fill: '#ffffff', fontWeight: 'bold' }),
      });
      capacityText.anchor.set(0.5);
      container.addChild(capacityText);

      this.stationLayer.addChild(container);

      this.upgradeZones.push({
        gfx: container,
        upgradeType: cfg.upgradeType,
        x: cfg.x,
        y: cfg.y,
        labelText,
        priceText,
        capacityText,
        playerInside: false,
      });
    }
  }

  private updateUpgradeZones() {
    for (const zone of this.upgradeZones) {
      const inside = this.dist(this.playerX, this.playerY, zone.x, zone.y) < 30;

      if (inside && !zone.playerInside) {
        zone.playerInside = true;
        if (this.gs.upgradeBag(zone.upgradeType)) {
          const bagSize = zone.upgradeType === 'player' ? this.gs.bagPlayer
            : zone.upgradeType === 'ajudante' ? this.gs.bagAjudante
            : zone.upgradeType === 'garcom' ? this.gs.bagGarcom : this.gs.bagContador;
          zone.capacityText.text = `Cap: ${bagSize}`;
          this.spawnFloatingText(zone.x, zone.y - 30, `Bag +1 (${bagSize})`, '#44ff44');
        }
      } else if (!inside) {
        zone.playerInside = false;
      }

      const bagSize = zone.upgradeType === 'player' ? this.gs.bagPlayer
        : zone.upgradeType === 'ajudante' ? this.gs.bagAjudante
        : zone.upgradeType === 'garcom' ? this.gs.bagGarcom : this.gs.bagContador;
      zone.capacityText.text = `Cap: ${bagSize}`;
    }
  }

  // ─── Defense Upgrade Zones (near banco) ───

  private buildDefenseUpgradeZones() {
    const baseX = MAP.banco.x;
    const baseY = MAP.banco.y - MAP.banco.h / 2 - 50;
    const spacing = 70;

    const configs: { type: string; x: number; y: number; color: number; labelStr: string; priceStr: string }[] = [
      {
        type: 'cerca',
        x: baseX - spacing,
        y: baseY,
        color: COLORS.cerca,
        labelStr: 'Upgrade Cerca',
        priceStr: this.getCercaUpgradeCostStr(),
      },
      {
        type: 'descanso',
        x: baseX,
        y: baseY,
        color: COLORS.restArea,
        labelStr: 'Upgrade Desc.',
        priceStr: this.getRestUpgradeCostStr(),
      },
      {
        type: 'escudo',
        x: baseX + spacing,
        y: baseY,
        color: 0x6666ff,
        labelStr: this.gs.hasShield ? 'Upg. Escudo' : 'Escudo Orbital',
        priceStr: this.getShieldCostStr(),
      },
    ];

    for (const cfg of configs) {
      const container = new Container();
      container.x = cfg.x;
      container.y = cfg.y;

      const bg = new Graphics();
      bg.roundRect(-30, -18, 60, 36, 4).fill({ color: cfg.color, alpha: 0.25 });
      bg.stroke({ color: cfg.color, width: 1.5 });
      container.addChild(bg);

      const labelText = new Text({
        text: cfg.labelStr,
        style: new TextStyle({ fontSize: 8, fill: '#ffffff', fontWeight: 'bold' }),
      });
      labelText.anchor.set(0.5);
      labelText.y = -6;
      container.addChild(labelText);

      const priceText = new Text({
        text: cfg.priceStr,
        style: new TextStyle({ fontSize: 9, fill: '#ffcc00' }),
      });
      priceText.anchor.set(0.5);
      priceText.y = 8;
      container.addChild(priceText);

      this.stationLayer.addChild(container);

      this.upgradeStationZones.push({
        gfx: container,
        type: cfg.type,
        x: cfg.x,
        y: cfg.y,
        labelText,
        priceText,
        playerInside: false,
      });
    }
  }

  private getCercaUpgradeCostStr(): string {
    if (this.gs.cercaLevel >= FENCE.upgrades.length) return 'MAX';
    return `${FENCE.upgrades[this.gs.cercaLevel].custoPrata}P`;
  }

  private getRestUpgradeCostStr(): string {
    if (this.gs.restLevel >= REST_AREA.upgrades.length) return 'MAX';
    return `${REST_AREA.upgrades[this.gs.restLevel].custoPrata}P`;
  }

  private getShieldCostStr(): string {
    if (!this.gs.hasShield) return `${ORBITAL_SHIELD.custoPrata}P`;
    if (this.gs.shieldLevel >= ORBITAL_SHIELD.upgrades.length) return 'MAX';
    return `${ORBITAL_SHIELD.upgrades[this.gs.shieldLevel].custoPrata}P`;
  }

  private updateDefenseUpgradeZones() {
    for (const zone of this.upgradeStationZones) {
      const inside = this.dist(this.playerX, this.playerY, zone.x, zone.y) < 35;

      if (inside && !zone.playerInside) {
        zone.playerInside = true;

        switch (zone.type) {
          case 'cerca':
            if (this.gs.upgradeCerca()) {
              this.spawnFloatingText(zone.x, zone.y - 30, `Cerca +${FENCE.upgrades[this.gs.cercaLevel - 1].bonusHp} HP!`, '#44ff44');
            }
            break;
          case 'descanso':
            if (this.gs.upgradeRest()) {
              this.spawnFloatingText(zone.x, zone.y - 30, `Descanso +${REST_AREA.upgrades[this.gs.restLevel - 1].bonusCapacidade} vagas!`, '#44ff44');
            }
            break;
          case 'escudo':
            if (!this.gs.hasShield) {
              if (this.gs.buyShield()) {
                this.buildShieldVisual();
                this.spawnFloatingText(zone.x, zone.y - 30, 'Escudo Orbital!', '#6666ff');
              }
            } else {
              if (this.gs.upgradeShield()) {
                this.spawnFloatingText(zone.x, zone.y - 30, 'Escudo melhorado!', '#6666ff');
              }
            }
            break;
        }
      } else if (!inside) {
        zone.playerInside = false;
      }

      // Update labels
      switch (zone.type) {
        case 'cerca':
          zone.priceText.text = this.getCercaUpgradeCostStr();
          zone.labelText.text = this.gs.cercaLevel >= FENCE.upgrades.length ? 'Cerca MAX' : 'Upgrade Cerca';
          break;
        case 'descanso':
          zone.priceText.text = this.getRestUpgradeCostStr();
          zone.labelText.text = this.gs.restLevel >= REST_AREA.upgrades.length ? 'Desc. MAX' : 'Upgrade Desc.';
          break;
        case 'escudo':
          zone.priceText.text = this.getShieldCostStr();
          zone.labelText.text = this.gs.hasShield
            ? (this.gs.shieldLevel >= ORBITAL_SHIELD.upgrades.length ? 'Escudo MAX' : 'Upg. Escudo')
            : 'Escudo Orbital';
          break;
      }
    }
  }

  // ─── Escudo Orbital ───

  private buildShieldVisual() {
    if (this.shieldGfx) return;
    this.shieldGfx = new Graphics();
    this.shieldGfx.zIndex = 10;
    this.entityLayer.addChild(this.shieldGfx);
  }

  private updateShield(dt: number) {
    if (!this.gs.hasShield || !this.shieldGfx) return;

    this.shieldAngle += dt * 2.5;
    const radius = 40;
    const shieldX = this.playerX + Math.cos(this.shieldAngle) * radius;
    const shieldY = this.playerY + Math.sin(this.shieldAngle) * radius;

    this.shieldGfx.clear();
    this.shieldGfx.circle(shieldX, shieldY, 10).fill({ color: 0x4488ff, alpha: 0.7 });
    this.shieldGfx.circle(shieldX, shieldY, 12).stroke({ color: 0x88bbff, width: 2, alpha: 0.5 });

    // Damage monsters on contact
    for (const m of this.monsters) {
      if (!m.alive) continue;
      if (this.dist(shieldX, shieldY, m.x, m.y) < 20) {
        m.hp -= this.gs.shieldDano * dt;
        m.hpBar.setProgress(Math.max(0, m.hp / m.maxHp));
        if (m.hitFlash <= 0) {
          m.hitFlash = 0.1;
          m.anim.tint = 0x4488ff;
        }
        if (m.hp <= 0) {
          this.onMonsterKilled(m);
        }
      }
    }
  }

  // ─── Hire Zones ───

  private buildHireZones() {
    const configs: { type: WorkerType; color: number; iconStr: string; labelStr: string }[] = [
      { type: 'cacador',  color: COLORS.workerCacador,  iconStr: 'CA', labelStr: 'Cacador' },
      { type: 'ajudante', color: COLORS.workerAjudante, iconStr: 'AJ', labelStr: 'Ajudante' },
      { type: 'garcom',   color: COLORS.workerGarcom,   iconStr: 'GA', labelStr: 'Garcom' },
      { type: 'contador', color: COLORS.workerContador, iconStr: 'CO', labelStr: 'Contador' },
      { type: 'arqueiro', color: COLORS.workerArqueiro, iconStr: 'AR', labelStr: 'Arqueiro' },
    ];

    for (const cfg of configs) {
      const pos = MAP.hireZones[cfg.type];
      const hired = this.gs.getWorkerCount(cfg.type) >= this.gs.getMaxWorkers(cfg.type);

      const container = new Container();
      container.x = pos.x;
      container.y = pos.y;

      const bg = new Graphics();
      bg.roundRect(-24, -24, 48, 48, 6).fill({ color: cfg.color, alpha: 0.3 });
      bg.stroke({ color: cfg.color, width: 2 });
      container.addChild(bg);

      const icon = new Text({
        text: cfg.iconStr,
        style: new TextStyle({ fontSize: 20, fill: '#ffffff', fontWeight: 'bold' }),
      });
      icon.anchor.set(0.5);
      container.addChild(icon);

      const costBronze = this.gs.getHireCostBronze(cfg.type);
      const costPrata = this.gs.getHireCostPrata(cfg.type);
      const costStr = costBronze > 0 ? `${costBronze}B` : `${costPrata}P`;

      const labelText = new Text({
        text: hired ? 'Contratado!' : cfg.labelStr,
        style: new TextStyle({
          fontSize: 10,
          fill: hired ? '#44ff44' : '#ffffff',
          fontWeight: 'bold',
        }),
      });
      labelText.anchor.set(0.5);
      labelText.y = 34;
      container.addChild(labelText);

      const priceText = new Text({
        text: hired ? '' : costStr,
        style: new TextStyle({ fontSize: 10, fill: '#ffcc00' }),
      });
      priceText.anchor.set(0.5);
      priceText.y = 46;
      container.addChild(priceText);

      this.stationLayer.addChild(container);

      this.hireZones.push({
        gfx: container,
        type: cfg.type,
        x: pos.x,
        y: pos.y,
        hired,
        labelText,
        priceText,
      });
    }
  }

  private getWorkerName(type: WorkerType): string {
    switch (type) {
      case 'cacador':  return 'Cacador';
      case 'ajudante': return 'Ajudante';
      case 'garcom':   return 'Garcom';
      case 'contador': return 'Contador';
      case 'arqueiro': return 'Arqueiro';
    }
  }

  private getWorkerSpritePath(type: WorkerType): string {
    switch (type) {
      case 'cacador':  return SPRITES.workerCacador;
      case 'ajudante': return SPRITES.workerAjudante;
      case 'garcom':   return SPRITES.workerGarcom;
      case 'contador': return SPRITES.workerContador;
      case 'arqueiro': return SPRITES.workerArqueiro;
    }
  }

  private updateHireZones() {
    for (const zone of this.hireZones) {
      const count = this.gs.getWorkerCount(zone.type);
      const max = this.gs.getMaxWorkers(zone.type);

      if (max === 0) {
        zone.labelText.text = `${this.getWorkerName(zone.type)} (bloq)`;
        zone.labelText.style.fill = '#888888';
        zone.priceText.text = '';
        continue;
      }

      const allHired = count >= max;

      if (allHired !== zone.hired) {
        zone.hired = allHired;
        if (allHired) {
          zone.labelText.text = 'Contratado!';
          zone.labelText.style.fill = '#44ff44';
          zone.priceText.text = '';
        } else {
          const costBronze = this.gs.getHireCostBronze(zone.type);
          const costPrata = this.gs.getHireCostPrata(zone.type);
          const costStr = costBronze > 0 ? `${costBronze}B` : `${costPrata}P`;
          zone.labelText.text = `${this.getWorkerName(zone.type)} +1`;
          zone.labelText.style.fill = '#ffffff';
          zone.priceText.text = costStr;
        }
      }

      if (zone.hired) continue;

      if (this.dist(this.playerX, this.playerY, zone.x, zone.y) < 40) {
        if (this.gs.canHireWorker(zone.type)) {
          if (this.gs.hireWorker(zone.type)) {
            zone.hired = count + 1 >= max;
            if (zone.hired) {
              zone.labelText.text = 'Contratado!';
              zone.labelText.style.fill = '#44ff44';
              zone.priceText.text = '';
            }

            const bgChild = zone.gfx.children[0] as Graphics;
            const color = zone.type === 'cacador' ? COLORS.workerCacador
              : zone.type === 'ajudante' ? COLORS.workerAjudante
              : zone.type === 'garcom' ? COLORS.workerGarcom
              : zone.type === 'contador' ? COLORS.workerContador
              : COLORS.workerArqueiro;
            bgChild.clear();
            bgChild.roundRect(-24, -24, 48, 48, 6).fill({ color, alpha: 0.6 });
            bgChild.stroke({ color: 0x44ff44, width: 2 });

            this.spawnWorkerNPC(zone.type);
            this.spawnFloatingText(zone.x, zone.y - 30, `${this.getWorkerName(zone.type)} contratado!`, '#44ff44');
          }
        }
      }
    }
  }

  // ─── Worker NPCs ───

  private spawnWorkerNPC(type: WorkerType): WorkerNPC {
    const spritePath = this.getWorkerSpritePath(type);

    const container = new Container();

    const anim = createCharAnim(spritePath, 'down', SPRITE_SCALE.character);
    anim.gotoAndStop(0);
    container.addChild(anim);

    const nameLabel = this.getWorkerName(type);
    const name = new Text({
      text: nameLabel,
      style: new TextStyle({ fontSize: 9, fill: '#ffffff', fontWeight: 'bold' }),
    });
    name.anchor.set(0.5);
    name.y = -22;
    container.addChild(name);

    const carryText = new Text({
      text: '',
      style: new TextStyle({ fontSize: 8, fill: '#ffcc00', fontWeight: 'bold' }),
    });
    carryText.anchor.set(0.5);
    carryText.y = -32;
    container.addChild(carryText);

    const stateColor = type === 'cacador' ? '#ff8888'
      : type === 'ajudante' ? '#88ffcc'
      : type === 'garcom' ? '#88bbff'
      : type === 'contador' ? '#cc99ff' : '#aaff44';
    const stateText = new Text({
      text: '',
      style: new TextStyle({ fontSize: 7, fill: stateColor }),
    });
    stateText.anchor.set(0.5);
    stateText.y = 16;
    container.addChild(stateText);

    const vigorBar = new ProgressBar({
      width: 24, height: 3,
      fillColor: 0x44cc44, bgColor: 0x333333,
      showText: false,
    });
    vigorBar.x = -12;
    vigorBar.y = -38;
    vigorBar.setProgress(1);
    container.addChild(vigorBar);

    const hirePos = MAP.hireZones[type];
    container.x = hirePos.x;
    container.y = hirePos.y - 40;

    this.entityLayer.addChild(container);

    const worker: WorkerNPC = {
      gfx: container,
      anim,
      spritePath,
      type,
      x: container.x,
      y: container.y,
      dir: 'down',
      state: 'idle',
      stateTimer: 0.5,
      targetX: 0,
      targetY: 0,
      carrying: 0,
      carryText,
      stateText,
      targetAnimal: null,
      targetItem: null,
      targetClient: null,
      targetCoin: null,
      attackCooldown: 0,
      vigor: VIGOR.max,
      vigorBar,
      preRestState: 'idle',
    };

    this.workers.push(worker);
    return worker;
  }

  private restoreWorkers() {
    for (let i = 0; i < this.gs.workerCacador; i++) this.spawnWorkerNPC('cacador');
    for (let i = 0; i < this.gs.workerAjudante; i++) this.spawnWorkerNPC('ajudante');
    for (let i = 0; i < this.gs.workerGarcom; i++) this.spawnWorkerNPC('garcom');
    for (let i = 0; i < this.gs.workerContador; i++) this.spawnWorkerNPC('contador');
    for (let i = 0; i < this.gs.workerArqueiro; i++) this.spawnWorkerNPC('arqueiro');
  }

  private updateWorkers(dt: number) {
    for (const w of this.workers) {
      // ─── Fadiga ───
      if (w.state !== 'resting' && w.state !== 'walking_to_rest') {
        w.vigor -= VIGOR.drainPerSec * dt;
        if (w.vigor < 0) w.vigor = 0;

        if (w.vigor <= VIGOR.restThreshold) {
          w.preRestState = w.state;
          if (w.targetAnimal) { w.targetAnimal.claimed = false; w.targetAnimal = null; }
          if (w.targetItem) { w.targetItem.claimed = false; w.targetItem = null; }
          if (w.targetClient) { w.targetClient.claimedByGarcom = false; w.targetClient = null; }
          if (w.targetCoin) { w.targetCoin.claimed = false; w.targetCoin = null; }
          w.carrying = 0;
          w.targetX = MAP.restArea.x + (Math.random() - 0.5) * 40;
          w.targetY = MAP.restArea.y + (Math.random() - 0.5) * 20;
          w.state = 'walking_to_rest';
        }
      }

      w.vigorBar.setProgress(w.vigor / VIGOR.max);
      if (w.vigor > 50) {
        w.vigorBar.setFillColor(0x44cc44);
      } else if (w.vigor > 20) {
        w.vigorBar.setFillColor(0xcccc44);
      } else {
        w.vigorBar.setFillColor(0xcc4444);
      }

      if (w.state === 'walking_to_rest') {
        const arrived = this.moveToward(w, dt, WORKER_SPEED * 0.7, 15);
        if (arrived) {
          w.state = 'resting';
          w.stateTimer = VIGOR.restDuration;
          w.anim.gotoAndStop(0);
        }
        this.updateWorkerVisual(w, dt);
        continue;
      }

      if (w.state === 'resting') {
        w.stateTimer -= dt;
        w.vigor += (VIGOR.max / VIGOR.restDuration) * dt;
        if (w.vigor > VIGOR.max) w.vigor = VIGOR.max;

        if (w.stateTimer <= 0) {
          w.vigor = VIGOR.max;
          w.state = 'idle';
          w.stateTimer = 0.3;
          this.spawnFloatingText(w.x, w.y - 20, 'Descansado!', '#44ff44');
        }
        this.updateWorkerVisual(w, dt);
        continue;
      }

      switch (w.type) {
        case 'cacador': this.updateWorkerCacador(w, dt); break;
        case 'ajudante': this.updateWorkerAjudante(w, dt); break;
        case 'garcom': this.updateWorkerGarcom(w, dt); break;
        case 'contador': this.updateWorkerContador(w, dt); break;
        case 'arqueiro': this.updateWorkerArqueiro(w, dt); break;
      }

      this.updateWorkerVisual(w, dt);
    }
  }

  private updateWorkerVisual(w: WorkerNPC, _dt: number) {
    w.gfx.x = w.x;
    w.gfx.y = w.y;

    if (w.carrying > 0) {
      if (w.type === 'contador') {
        w.carryText.text = `${w.carrying}B`;
      } else {
        const typeLabel = w.type === 'garcom' ? 'Assada' : 'Crua';
        w.carryText.text = `${typeLabel} x${w.carrying}`;
      }
    } else {
      w.carryText.text = '';
    }

    w.stateText.text = this.getWorkerStateLabel(w);
  }

  private getWorkerStateLabel(w: WorkerNPC): string {
    if (w.state === 'walking_to_rest') return 'Cansado...';
    if (w.state === 'resting') return 'zzZ';

    switch (w.type) {
      case 'cacador':
        switch (w.state) {
          case 'idle': case 'cooldown': return 'Esperando...';
          case 'seeking': return 'Indo cacar';
          case 'attacking': return 'Atacando!';
          default: return '';
        }
      case 'ajudante':
        switch (w.state) {
          case 'idle': return 'Esperando...';
          case 'seeking_meat': return 'Buscando carne';
          case 'picking_up': return 'Pegando';
          case 'walking_to_assador': return 'Levando ao assador';
          case 'depositing': return 'Depositando';
          default: return '';
        }
      case 'garcom':
        switch (w.state) {
          case 'idle': return 'Esperando...';
          case 'walking_to_assador': return 'Indo pegar assada';
          case 'picking_up': return 'Pegando';
          case 'walking_to_client': return 'Servindo cliente';
          case 'serving': return 'Entregando!';
          case 'returning_to_assador': return 'Voltando ao assador';
          case 'walking_to_balcao': return 'Levando ao balcao';
          case 'depositing': return 'Depositando';
          default: return '';
        }
      case 'contador':
        switch (w.state) {
          case 'idle': return 'Esperando...';
          case 'seeking_coin': return 'Buscando moeda';
          case 'picking_up': return 'Pegando';
          case 'walking_to_banco': return 'Levando ao banco';
          case 'depositing': return 'Depositando';
          default: return '';
        }
      case 'arqueiro':
        switch (w.state) {
          case 'idle': return 'Em torre';
          default: return '';
        }
    }
  }

  // ─── Cacador AI ───

  private updateWorkerCacador(w: WorkerNPC, dt: number) {
    switch (w.state) {
      case 'idle': {
        w.stateTimer -= dt;
        if (w.stateTimer <= 0) {
          const target = this.findNearestAliveAnimal(w.x, w.y);
          if (target) {
            target.claimed = true;
            w.targetAnimal = target;
            w.targetX = target.x;
            w.targetY = target.y;
            w.state = 'seeking';
          } else {
            w.stateTimer = 0.5;
          }
        }
        break;
      }

      case 'seeking': {
        if (!w.targetAnimal || !w.targetAnimal.alive) {
          if (w.targetAnimal) w.targetAnimal.claimed = false;
          w.targetAnimal = null;
          w.state = 'idle';
          w.stateTimer = 0.3;
          break;
        }

        w.targetX = w.targetAnimal.x;
        w.targetY = w.targetAnimal.y;

        const arrived = this.moveToward(w, dt, CACADOR_SPEED, 30);
        if (arrived) {
          w.state = 'attacking';
          w.attackCooldown = 0;
        }
        break;
      }

      case 'attacking': {
        if (!w.targetAnimal || !w.targetAnimal.alive) {
          if (w.targetAnimal) w.targetAnimal.claimed = false;
          w.targetAnimal = null;
          w.state = 'cooldown';
          w.stateTimer = 1;
          break;
        }

        w.targetX = w.targetAnimal.x;
        w.targetY = w.targetAnimal.y;
        this.moveToward(w, dt, CACADOR_SPEED * 0.5, 25);

        w.attackCooldown -= dt;
        if (w.attackCooldown <= 0) {
          w.attackCooldown = 0.5;
          this.attackAnimal(w.targetAnimal, false);

          if (!w.targetAnimal.alive) {
            w.targetAnimal.claimed = false;
            w.targetAnimal = null;
            w.state = 'cooldown';
            w.stateTimer = 1;
          }
        }
        break;
      }

      case 'cooldown': {
        w.stateTimer -= dt;
        if (w.stateTimer <= 0) {
          w.state = 'idle';
          w.stateTimer = 0.5;
        }
        break;
      }

      default:
        w.state = 'idle';
        w.stateTimer = 0.5;
    }
  }

  // ─── Ajudante AI ───

  private updateWorkerAjudante(w: WorkerNPC, dt: number) {
    switch (w.state) {
      case 'idle': {
        w.stateTimer -= dt;
        if (w.stateTimer <= 0) {
          const item = this.findNearestGroundItem(w.x, w.y, 'carne_crua');
          if (item) {
            item.claimed = true;
            w.targetItem = item;
            w.targetX = item.x;
            w.targetY = item.y;
            w.state = 'seeking_meat';
          } else {
            w.stateTimer = 0.3;
          }
        }
        break;
      }

      case 'seeking_meat': {
        if (!w.targetItem || !this.groundItems.includes(w.targetItem)) {
          if (w.targetItem) w.targetItem.claimed = false;
          w.targetItem = null;
          w.state = 'idle';
          w.stateTimer = 0.3;
          break;
        }

        const arrived = this.moveToward(w, dt, WORKER_SPEED, 15);
        if (arrived) {
          w.state = 'picking_up';
          w.stateTimer = 0;
        }
        break;
      }

      case 'picking_up': {
        if (w.targetItem && this.groundItems.includes(w.targetItem)) {
          const idx = this.groundItems.indexOf(w.targetItem);
          this.itemLayer.removeChild(w.targetItem.gfx);
          this.groundItems.splice(idx, 1);
          w.carrying++;
        }
        w.targetItem = null;

        if (w.carrying < this.gs.bagAjudante) {
          const nextItem = this.findNearestGroundItem(w.x, w.y, 'carne_crua');
          if (nextItem) {
            nextItem.claimed = true;
            w.targetItem = nextItem;
            w.targetX = nextItem.x;
            w.targetY = nextItem.y;
            w.state = 'seeking_meat';
            break;
          }
        }

        if (w.carrying > 0) {
          w.targetX = MAP.assador.entradaX;
          w.targetY = MAP.assador.y;
          w.state = 'walking_to_assador';
        } else {
          w.state = 'idle';
          w.stateTimer = 0.3;
        }
        break;
      }

      case 'walking_to_assador': {
        const arrived = this.moveToward(w, dt, WORKER_SPEED, 15);
        if (arrived) {
          w.state = 'depositing';
          w.stateTimer = 0;
        }
        break;
      }

      case 'depositing': {
        if (w.carrying > 0) {
          this.gs.depositCarneNaMesa(w.carrying);
          this.spawnFloatingText(MAP.assador.entradaX, MAP.assador.y - 30, `+${w.carrying} Crua`, '#ff6666');
          w.carrying = 0;
        }
        w.state = 'idle';
        w.stateTimer = 0.3;
        break;
      }

      default:
        w.state = 'idle';
        w.stateTimer = 0.3;
    }
  }

  // ─── Garcom AI ───

  private updateWorkerGarcom(w: WorkerNPC, dt: number) {
    switch (w.state) {
      case 'idle': {
        w.stateTimer -= dt;
        if (w.stateTimer <= 0) {
          if (this.gs.bancadaCarne > 0) {
            w.targetX = MAP.assador.saidaX;
            w.targetY = MAP.assador.y;
            w.state = 'walking_to_assador';
          } else if (this.dist(w.x, w.y, MAP.assador.saidaX + 15, MAP.assador.y) > 50) {
            w.targetX = MAP.assador.saidaX + 15;
            w.targetY = MAP.assador.y;
            w.state = 'returning_to_assador';
          } else {
            w.stateTimer = 0.3;
          }
        }
        break;
      }

      case 'walking_to_assador': {
        const arrived = this.moveToward(w, dt, WORKER_SPEED, 15);
        if (arrived) {
          w.state = 'picking_up';
          w.stateTimer = 0;
        }
        break;
      }

      case 'picking_up': {
        if (this.gs.bancadaCarne > 0) {
          const toPickup = Math.min(this.gs.bancadaCarne, this.gs.bagGarcom);
          this.gs.bancadaCarne -= toPickup;
          w.carrying = toPickup;
          this.spawnFloatingText(MAP.assador.saidaX, MAP.assador.y - 30, `Pegou ${toPickup} Assada`, '#ffaa00');
        }

        if (w.carrying > 0) {
          const client = this.findFirstWaitingClient();
          if (client) {
            client.claimedByGarcom = true;
            w.targetClient = client;
            w.targetX = client.x;
            w.targetY = client.y;
            w.state = 'walking_to_client';
          } else {
            w.targetX = MAP.balcaoVenda.x;
            w.targetY = MAP.balcaoVenda.y;
            w.state = 'walking_to_balcao';
          }
        } else {
          w.state = 'idle';
          w.stateTimer = 0.3;
        }
        break;
      }

      case 'walking_to_client': {
        if (!w.targetClient || !this.clients.includes(w.targetClient) ||
            (w.targetClient.state !== 'waiting' && w.targetClient.state !== 'walking')) {
          if (w.targetClient) w.targetClient.claimedByGarcom = false;
          w.targetClient = null;

          const nextClient = this.findFirstWaitingClient();
          if (nextClient) {
            nextClient.claimedByGarcom = true;
            w.targetClient = nextClient;
            w.targetX = nextClient.x;
            w.targetY = nextClient.y;
          } else {
            w.targetX = MAP.balcaoVenda.x;
            w.targetY = MAP.balcaoVenda.y;
            w.state = 'walking_to_balcao';
            break;
          }
        }

        w.targetX = w.targetClient!.x;
        w.targetY = w.targetClient!.y;

        const arrived = this.moveToward(w, dt, WORKER_SPEED, 20);
        if (arrived) {
          w.state = 'serving';
          w.stateTimer = 0;
        }
        break;
      }

      case 'serving': {
        const client = w.targetClient;
        if (client && this.clients.includes(client) && client.state === 'waiting') {
          client.served = true;
          client.state = 'eating';
          client.timer = CLIENT_EAT_TIME;
          client.claimedByGarcom = false;
          client.anim.tint = 0xffffff;
          this.spawnFloatingText(client.x, client.y - 20, 'Servido!', '#44ff44');
          w.carrying--;
        } else if (client) {
          client.claimedByGarcom = false;
        }
        w.targetClient = null;

        if (w.carrying > 0) {
          const nextClient = this.findFirstWaitingClient();
          if (nextClient) {
            nextClient.claimedByGarcom = true;
            w.targetClient = nextClient;
            w.targetX = nextClient.x;
            w.targetY = nextClient.y;
            w.state = 'walking_to_client';
          } else {
            w.targetX = MAP.balcaoVenda.x;
            w.targetY = MAP.balcaoVenda.y;
            w.state = 'walking_to_balcao';
          }
        } else {
          w.targetX = MAP.assador.saidaX + 15;
          w.targetY = MAP.assador.y;
          w.state = 'returning_to_assador';
        }
        break;
      }

      case 'walking_to_balcao': {
        const arrived = this.moveToward(w, dt, WORKER_SPEED, 15);
        if (arrived) {
          w.state = 'depositing';
          w.stateTimer = 0;
        }
        break;
      }

      case 'depositing': {
        if (w.carrying > 0) {
          this.gs.depositCarneNoBalcao(w.carrying);
          this.spawnFloatingText(MAP.balcaoVenda.x, MAP.balcaoVenda.y - 20, `+${w.carrying} Assada`, '#ffaa00');
          w.carrying = 0;
        }
        w.targetX = MAP.assador.saidaX + 15;
        w.targetY = MAP.assador.y;
        w.state = 'returning_to_assador';
        break;
      }

      case 'returning_to_assador': {
        if (this.gs.bancadaCarne > 0) {
          w.targetX = MAP.assador.saidaX;
          w.targetY = MAP.assador.y;
          w.state = 'walking_to_assador';
          break;
        }
        const arrived = this.moveToward(w, dt, WORKER_SPEED, 15);
        if (arrived) {
          w.state = 'idle';
          w.stateTimer = 0.3;
        }
        break;
      }

      default:
        w.state = 'idle';
        w.stateTimer = 0.3;
    }
  }

  // ─── Contador AI ───

  private updateWorkerContador(w: WorkerNPC, dt: number) {
    switch (w.state) {
      case 'idle': {
        w.stateTimer -= dt;
        if (w.stateTimer <= 0) {
          const coin = this.findNearestCoin(w.x, w.y);
          if (coin) {
            coin.claimed = true;
            w.targetCoin = coin;
            w.targetX = coin.x;
            w.targetY = coin.y;
            w.state = 'seeking_coin';
          } else {
            w.stateTimer = 0.5;
          }
        }
        break;
      }

      case 'seeking_coin': {
        if (!w.targetCoin || !this.coins.includes(w.targetCoin)) {
          if (w.targetCoin) w.targetCoin.claimed = false;
          w.targetCoin = null;
          w.state = 'idle';
          w.stateTimer = 0.3;
          break;
        }

        w.targetX = w.targetCoin.x;
        w.targetY = w.targetCoin.y;

        const arrived = this.moveToward(w, dt, WORKER_SPEED, 15);
        if (arrived) {
          w.state = 'picking_up';
          w.stateTimer = 0;
        }
        break;
      }

      case 'picking_up': {
        if (w.targetCoin && this.coins.includes(w.targetCoin)) {
          const idx = this.coins.indexOf(w.targetCoin);
          this.itemLayer.removeChild(w.targetCoin.gfx);
          this.coins.splice(idx, 1);
          w.carrying += CARNE_SELL_VALUE;
          this.spawnFloatingText(w.x, w.y - 20, `Pegou ${CARNE_SELL_VALUE}B`, '#ffd700');
        }
        w.targetCoin = null;

        const coinsPerBag = this.gs.bagContador;
        const coinsCarried = Math.ceil(w.carrying / CARNE_SELL_VALUE);
        if (coinsCarried < coinsPerBag) {
          const nextCoin = this.findNearestCoin(w.x, w.y);
          if (nextCoin) {
            nextCoin.claimed = true;
            w.targetCoin = nextCoin;
            w.targetX = nextCoin.x;
            w.targetY = nextCoin.y;
            w.state = 'seeking_coin';
            break;
          }
        }

        w.targetX = MAP.banco.x;
        w.targetY = MAP.banco.y;
        w.state = 'walking_to_banco';
        break;
      }

      case 'walking_to_banco': {
        const arrived = this.moveToward(w, dt, WORKER_SPEED, 20);
        if (arrived) {
          w.state = 'depositing';
          w.stateTimer = 0;
        }
        break;
      }

      case 'depositing': {
        if (w.carrying > 0) {
          this.gs.bronze += w.carrying;
          this.spawnFloatingText(MAP.banco.x, MAP.banco.y - 30, `+${w.carrying}B`, '#ffd700');
          w.carrying = 0;
        }
        w.state = 'idle';
        w.stateTimer = 0.3;
        break;
      }

      default:
        w.state = 'idle';
        w.stateTimer = 0.5;
    }
  }

  // ─── Arqueiro AI ───

  private updateWorkerArqueiro(w: WorkerNPC, _dt: number) {
    if (w.state !== 'idle') {
      w.state = 'idle';
    }
    const towerIdx = this.workers.filter(ww => ww.type === 'arqueiro').indexOf(w);
    if (towerIdx >= 0 && towerIdx < MAP.towers.length) {
      const tp = MAP.towers[towerIdx];
      w.x = tp.x;
      w.y = tp.y - 16;
    }
  }

  private findFirstWaitingClient(): Client | null {
    for (const c of this.clients) {
      if (c.state === 'waiting' && !c.claimedByGarcom) return c;
    }
    return null;
  }

  // ─── Worker Helpers ───

  private moveToward(w: WorkerNPC, dt: number, speed: number, threshold: number): boolean {
    const dx = w.targetX - w.x;
    const dy = w.targetY - w.y;
    const d = Math.sqrt(dx * dx + dy * dy);
    if (d < threshold) {
      w.anim.gotoAndStop(0);
      return true;
    }

    const factor = speed * dt / d;
    w.x += dx * factor;
    w.y += dy * factor;

    w.x = Math.max(14, Math.min(MAP.worldW - 14, w.x));
    w.y = Math.max(14, Math.min(MAP.worldH - 14, w.y));

    const newDir: Dir = Math.abs(dx) > Math.abs(dy)
      ? (dx > 0 ? 'right' : 'left')
      : (dy > 0 ? 'down' : 'up');
    if (newDir !== w.dir) {
      w.dir = newDir;
      updateCharDir(w.anim, w.spritePath, w.dir);
    }
    if (!w.anim.playing) w.anim.play();

    return false;
  }

  private findNearestAliveAnimal(fromX: number, fromY: number): Animal | null {
    let best: Animal | null = null;
    let bestDist = Infinity;
    for (const a of this.animals) {
      if (!a.alive || a.claimed) continue;
      const d = this.dist(fromX, fromY, a.x, a.y);
      if (d < bestDist) {
        bestDist = d;
        best = a;
      }
    }
    return best;
  }

  private findNearestCoin(fromX: number, fromY: number): CoinItem | null {
    let best: CoinItem | null = null;
    let bestDist = Infinity;
    for (const coin of this.coins) {
      if (coin.claimed) continue;
      const d = this.dist(fromX, fromY, coin.x, coin.y);
      if (d < bestDist) {
        bestDist = d;
        best = coin;
      }
    }
    return best;
  }

  private findNearestGroundItem(fromX: number, fromY: number, type: InventoryType): GroundItem | null {
    let best: GroundItem | null = null;
    let bestDist = Infinity;
    for (const item of this.groundItems) {
      if (item.type !== type || item.claimed) continue;
      const d = this.dist(fromX, fromY, item.x, item.y);
      if (d < bestDist) {
        bestDist = d;
        best = item;
      }
    }
    return best;
  }

  // ─── Helpers ───

  private dist(x1: number, y1: number, x2: number, y2: number): number {
    const dx = x1 - x2;
    const dy = y1 - y2;
    return Math.sqrt(dx * dx + dy * dy);
  }
}
